import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  UserPlus, Users, ChevronRight, X, Plus, Search, CheckCircle2, Circle,
  Trash2, Edit3, ArrowRight, Phone, Mail, MapPin, Upload as UploadIcon,
  UserCheck, AlertCircle, Filter, FileText, ExternalLink, BookOpen, Compass, Award,
  GraduationCap, Undo2, Download, LayoutGrid, List, ArrowUpDown, AlertTriangle,
  ArrowLeftRight, Archive, QrCode, Copy, Printer, Clock, MessageSquare, RefreshCw, Shuffle,
  Link2 as LinkIcon, Check, RotateCcw,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { StatCard, EmptyState, ProgressRing } from "@/components/crm/UiBits";
import ActivityLogPanel from "@/components/crm/ActivityLogPanel";
import {
  STAGES, STAGE_META, TRACKS, TRACK_FIELD, resourceKindsFor,
  AGE_GROUPS, todayIso, sessionTitle, sessionDescription,
} from "@/lib/tracks";
import { DescriptionCell } from "@/components/DescriptionCell";
import ChurchToolsSwitcher from "@/components/ChurchToolsSwitcher";
import GenderField from "@/components/GenderField";
import ConnectionLog from "@/components/crm/ConnectionLog";
import { formatDisplayName } from "@/lib/names";
import DirectoryPicker from "@/components/crm/DirectoryPicker";
import AddDirectoryPersonDialog from "@/components/crm/AddDirectoryPersonDialog";
import AssignmentsPanel from "@/components/crm/AssignmentsPanel";
import ReviewQueue from "@/components/crm/ReviewQueue";
import { TrackerToolbar, TabBar, SummaryBar } from "@/components/crm/TrackerUI";
import TextExamplesModal from "@/components/crm/TextExamplesModal";
import TextGuestButton from "@/components/crm/TextGuestButton";
import {
  NS_LESSON_TITLES, discipleDisplayName, discipleSortKey, AttendanceGrid,
  LessonResourcesLibrary, LessonTitleLink, NsDiscipleRow, LessonLinkBoxes, NsClassCard,
} from "@/components/crm/NextStepsBits";

// First Steps page only counts the pre-Next-Steps tracks: Initial contact + Introduction + Home study.
// The "Next Steps" 24-lesson series lives on its own tab and is excluded from First Steps progress.
// Either/or sections (e.g. Introduction to the Gospel) count as `requiredCount`, not `count`.
// Guest Connection is now an open-ended log (see ConnectionLog.jsx) — it no
// longer participates in the "X of N" First Steps progress rings. Home / Intro
// still count. Next Steps (first_steps) is its own phase.
const FIRST_STEPS_TRACKS = TRACKS.filter((t) => t.key !== "first_steps" && t.key !== "initial_contact");
const FIRST_STEPS_TOTAL = FIRST_STEPS_TRACKS.reduce((s, t) => s + (t.requiredCount ?? t.count), 0);

// ---------- Local helpers ----------
const emptyGuest = () => ({
  first_name: "", last_name: "", gender: null, email: "", phone: "", address_city: "",
  age_group: "", referral_source: "", invited_by: "", notes: "",
});
const emptyTeacher = () => ({
  name: "", email: "", phone: "", city: "", age_group_pref: "",
  interests: [], active: true, notes: "", password: "",
});

// ==================== GUEST PROFILE MODAL ====================
function LessonResource({ trackKey, index, kind, label, url }) {
  if (!url) return null;
  return (
    <div className="mt-1 flex items-center gap-1" data-testid={`resource-view-${trackKey}-${index}-${kind}`}>
      <a href={url} target="_blank" rel="noreferrer" className="text-[0.7rem] flex items-center gap-1 hover:underline" style={{ color: "var(--terracotta)" }} data-testid={`resource-link-${trackKey}-${index}-${kind}`}>
        <FileText size={11} /> {label} <ExternalLink size={9} />
      </a>
    </div>
  );
}

function ProgressTrack({ track, sessions, onToggle, resources = {}, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  const Icon = track.icon;
  const rawDone = sessions.filter((s) => s.completed).length;
  const required = track.requiredCount ?? track.count;
  // Either/or sections cap the visible progress at `required`.
  const done = Math.min(rawDone, required);
  const eitherOr = required < track.count;
  const showItemSubtitle = track.key !== "initial_contact";
  const pct = (done / required) * 100;
  return (
    <div className="mb-3 rounded-lg" style={{ border: "1px solid var(--border-subtle)", background: open ? "transparent" : "var(--bg-alt)" }} data-testid={`track-${track.key}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-3 text-left" aria-expanded={open} data-testid={`track-toggle-${track.key}`}>
        <div className="shrink-0 flex items-center justify-center rounded-md" style={{ width: 32, height: 32, background: "var(--surface-hover)", color: track.color }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <h4 className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>{track.label}</h4>
        <span className="text-xs" style={{ color: "var(--muted)" }}>{done}/{required}</span>
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-base)" }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: track.color, transition: "width 0.3s ease" }} />
        </div>
        {done >= required ? <CheckCircle2 size={16} style={{ color: track.color }} /> : null}
        <ChevronRight size={16} className="shrink-0 transition-transform" style={{ color: "var(--muted)", transform: open ? "rotate(90deg)" : "none" }} />
      </button>
      {open && <div className="px-3 pb-3">
      {eitherOr && (
        <p className="text-xs mb-2 pl-11" style={{ color: "var(--muted)" }} data-testid={`track-${track.key}-hint`}>
          {required === 1
            ? <>Pick <strong>one</strong> lesson — either satisfies this section.</>
            : <>Complete <strong>any {required} of {track.count}</strong> to satisfy this section.</>}
        </p>
      )}
      <div className="space-y-2">
        {sessions.map((s, i) => (
          <div key={i} className="flex flex-wrap sm:flex-nowrap items-start gap-3 p-3 rounded-md border" style={{ borderColor: s.completed ? "var(--border-strong)" : "var(--border-subtle)", background: s.completed ? "var(--surface-hover)" : "transparent" }} data-testid={`session-${track.key}-${i}`}>
            <button
              onClick={() => onToggle(track.key, i, !s.completed, s.date || todayIso(), s.notes || "")}
              className="mt-1 shrink-0"
              data-testid={`toggle-${track.key}-${i}`}
              aria-label={`Toggle ${track.key} session ${i + 1}`}
            >
              {s.completed
                ? <CheckCircle2 size={20} style={{ color: track.color }} />
                : <Circle size={20} style={{ color: "var(--muted)" }} />}
            </button>

            <div className="flex-1 sm:flex-none sm:w-52 min-w-0 sm:shrink-0">
              {(() => {
                const notesUrl = resources[`${track.key}:${i}:notes`] || resources[`${track.key}:${i}:teacher_notes`];
                return notesUrl ? (
                  <a href={notesUrl} target="_blank" rel="noreferrer" className="text-sm font-medium leading-tight inline-flex items-center gap-1 hover:underline" style={{ color: "var(--gold)" }} title="Open teacher notes" data-testid={`lesson-title-link-${track.key}-${i}`}>
                    {sessionTitle(track.key, i)} <ExternalLink size={11} className="shrink-0" />
                  </a>
                ) : (
                  <div className="text-sm font-medium leading-tight" style={{ color: "var(--ink)" }}>{sessionTitle(track.key, i)}</div>
                );
              })()}
              {sessionDescription(track.key, i) && (
                <DescriptionCell
                  title={sessionTitle(track.key, i)}
                  description={sessionDescription(track.key, i)}
                  testid={`desc-${track.key}-${i}`}
                />
              )}
              {showItemSubtitle && !sessionDescription(track.key, i) && (
                <div className="text-[0.65rem] uppercase tracking-wider mt-0.5" style={{ color: "var(--muted)" }}>
                  Session {i + 1}
                </div>
              )}
              {s.completed && (
                <input
                  type="date"
                  value={s.date || todayIso()}
                  onChange={(e) => onToggle(track.key, i, true, e.target.value, s.notes || "")}
                  data-testid={`date-${track.key}-${i}`}
                  className="mt-2 text-xs px-2 py-1 w-full"
                />
              )}
              {track.hasResources && resourceKindsFor(track.key, i).some((rk) => rk.key !== "notes" && rk.key !== "teacher_notes") && (
                <div className="mt-2 space-y-0.5">
                  {resourceKindsFor(track.key, i).filter((rk) => rk.key !== "notes" && rk.key !== "teacher_notes").map((rk) => (
                    <LessonResource
                      key={rk.key}
                      trackKey={track.key}
                      index={i}
                      kind={rk.key}
                      label={rk.label}
                      url={resources[`${track.key}:${i}:${rk.key}`] || ""}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="basis-full sm:basis-auto sm:flex-1 min-w-0">
              <textarea
                rows={2}
                placeholder={`Notes for "${sessionTitle(track.key, i)}"…`}
                defaultValue={s.notes || ""}
                onBlur={(e) => {
                  const next = e.target.value;
                  if (next !== (s.notes || "")) {
                    onToggle(track.key, i, s.completed, s.date || (s.completed ? todayIso() : null), next);
                  }
                }}
                data-testid={`notes-${track.key}-${i}`}
                className="w-full text-xs px-2 py-1.5"
              />
            </div>
          </div>
        ))}
      </div>
      </div>}
    </div>
  );
}

function GuestProfile({ guest, teachers, orgId, onClose, onSaved }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...guest });
  const [assigning, setAssigning] = useState(false);
  const [assignScope, setAssignScope] = useState(user?.is_admin ? "all" : "mine");
  const [localGuest, setLocalGuest] = useState(guest);
  const [resources, setResources] = useState({});

  useEffect(() => { setLocalGuest(guest); setForm({ ...guest }); }, [guest]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const activeTeachers = teachers.filter((t) => t.active);
  const orgParams = orgId ? { org_id: orgId } : {};

  useEffect(() => {
    api.get("/coord/lesson-resources", { params: orgParams }).then((r) => setResources(r.data.resources || {})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const toggleSession = async (track, index, completed, date, notes) => {
    const field = TRACK_FIELD[track];
    const updated = { ...localGuest };
    updated[field] = [...(updated[field] || [])];
    updated[field][index] = { completed, date: completed ? date : null, notes: notes || "" };
    setLocalGuest(updated);
    try {
      await api.put(`/coord/guests/${localGuest.id}/progress`, { track, index, completed, date, notes }, { params: orgParams });
      onSaved?.();
    } catch {
      toast.error("Couldn't save. Try again.");
    }
  };

  const saveEdits = async () => {
    try {
      const patch = { ...form };
      ["id", "progress", "stage", "assigned_teacher", "assigned_teachers", "assigned_teacher_ids", "assigned_teacher_id", "monday_sessions", "church_lessons", "home_lessons", "first_steps_lessons", "initial_contact_items", "created_at", "updated_at"].forEach((k) => delete patch[k]);
      await api.put(`/coord/guests/${localGuest.id}`, patch, { params: orgParams });
      toast.success("Saved.");
      setEditing(false);
      onSaved?.();
    } catch { toast.error("Couldn't save."); }
  };

  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [changingLifecycle, setChangingLifecycle] = useState(false);
  const expectedConfirmText = `${localGuest.first_name || ""} ${localGuest.last_name || ""}`.trim();
  const deleteGuest = async () => {
    if (deleteConfirmText.trim().toLowerCase() !== expectedConfirmText.toLowerCase()) {
      toast.error("Please type the guest's full name to confirm.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/coord/guests/${localGuest.id}`, { params: orgParams });
      toast.success(`${expectedConfirmText} deleted.`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
    } finally {
      setDeleting(false);
    }
  };

  const archiveGuest = async () => {
    setArchiving(true);
    try {
      await api.post(`/coord/guests/${localGuest.id}/archive`, null, { params: orgParams });
      toast.success(`${expectedConfirmText || "Profile"} archived. You can restore it later.`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't archive.");
    } finally {
      setArchiving(false);
    }
  };

  const setLifecycle = async (nextStatus) => {
    if (localGuest.lifecycle_status === nextStatus) return;
    setChangingLifecycle(true);
    const prev = localGuest.lifecycle_status || "guest";
    setLocalGuest({ ...localGuest, lifecycle_status: nextStatus });
    try {
      await api.put(`/coord/guests/${localGuest.id}/lifecycle`, { status: nextStatus }, { params: orgParams });
      const labels = { guest: "Guest", disciple: "Disciple", member: "Member" };
      toast.success(`${expectedConfirmText || "Profile"} is now a ${labels[nextStatus]}.`);
      onSaved?.();
    } catch (err) {
      setLocalGuest({ ...localGuest, lifecycle_status: prev });
      toast.error(err?.response?.data?.detail || "Couldn't change status.");
    } finally {
      setChangingLifecycle(false);
    }
  };

  const assignedIds = useMemo(() => {
    const list = localGuest.assigned_teacher_ids || (localGuest.assigned_teacher ? [localGuest.assigned_teacher.id] : []);
    return list.filter(Boolean);
  }, [localGuest]);

  const saveTeachers = async (nextIds) => {
    // Only send teacher IDs that actually exist in the current org's teacher list.
    // Legacy `assigned_teacher_ids` on a guest may point to teachers that were later
    // deleted or moved orgs — those ghosts must not be re-sent on save.
    const validIds = new Set(teachers.map((t) => t.id));
    const dedup = Array.from(new Set(nextIds.filter((tid) => tid && validIds.has(tid))));
    const nextAssigned = dedup.map((tid) => {
      const t = teachers.find((x) => x.id === tid);
      return t ? { id: t.id, name: t.name } : null;
    }).filter(Boolean);
    setLocalGuest({
      ...localGuest,
      assigned_teacher_ids: dedup,
      assigned_teacher_id: dedup[0] || null,
      assigned_teacher: nextAssigned[0] || null,
      assigned_teachers: nextAssigned,
    });
    try {
      const r = await api.put(`/coord/guests/${localGuest.id}/assign-teachers`, { teacher_ids: dedup }, { params: orgParams });
      const droppedCount = (r?.data?.dropped_ids || []).length;
      if (droppedCount > 0) {
        toast.warning(`${droppedCount} stale teacher assignment${droppedCount === 1 ? "" : "s"} cleaned up.`);
      } else {
        toast.success(dedup.length ? "Teachers updated." : "All teachers removed.");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update teachers.");
      // Revert on failure
      setLocalGuest(guest);
    }
  };

  const toggleTeacher = (tid) => {
    const current = new Set(assignedIds);
    if (current.has(tid)) current.delete(tid); else current.add(tid);
    saveTeachers([...current]);
  };

  const clearAll = () => saveTeachers([]);

  const visibleTeachers = useMemo(() => {
    if (assignScope === "all" || user?.is_admin) return activeTeachers;
    return activeTeachers.filter((t) => {
      const coordIds = [...(t.coordinator_ids || []), ...(t.coordinator_id ? [t.coordinator_id] : [])];
      return coordIds.includes(user?.id) || assignedIds.includes(t.id);
    });
  }, [activeTeachers, assignScope, user, assignedIds]);

  const stage = STAGE_META[localGuest.stage] || STAGE_META.new;

  const overallDone = FIRST_STEPS_TRACKS.reduce((sum, t) => {
    const rawDone = (localGuest[TRACK_FIELD[t.key]] || []).filter((s) => s.completed).length;
    return sum + Math.min(rawDone, t.requiredCount ?? t.count);
  }, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-6 lg:py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <div className="w-full max-w-4xl relative rounded-lg" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="guest-profile-modal">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 rounded-t-lg border-b px-6 lg:px-10 py-5 flex items-start justify-between gap-4 flex-wrap" style={{ background: "var(--bg-alt)", borderColor: "var(--border-subtle)" }}>
          <div className="flex items-center gap-4 min-w-0">
            <ProgressRing value={overallDone} total={FIRST_STEPS_TOTAL} size={54} stroke={5} color="var(--ember)" label="Overall progress" />
            <div className="min-w-0">
              <span className="inline-block px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.18em] rounded-full" style={{ background: stage.color, color: "#FFFFFF" }}>{stage.label}</span>
              <h2 className="font-serif-display text-2xl lg:text-3xl leading-tight mt-1" style={{ color: "var(--ink)" }}>
                {formatDisplayName(localGuest)}
              </h2>
              <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
                {overallDone} of {FIRST_STEPS_TOTAL} sessions complete
              </div>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {!editing && (
              <button onClick={() => setEditing(true)} className="btn-ghost" style={{ padding: "0.5rem 0.9rem" }} data-testid="guest-edit-btn">
                <Edit3 size={14} /> Edit
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid="guest-profile-close" aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-6 lg:px-10 py-6 lg:py-8">
          {/* Details */}
          {editing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 p-6 rounded-md border" style={{ borderColor: "var(--border-subtle)" }}>
              {["first_name","last_name","email","phone","address_city","age_group","referral_source","invited_by"].map((k) => (
                <div key={k}>
                  <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>{k.replace(/_/g, " ")}</label>
                  <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid={`guest-edit-${k}`} />
                </div>
              ))}
              <div className="md:col-span-2">
                <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="guest-edit-gender" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Notes</label>
                <textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="guest-edit-notes" />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <button onClick={saveEdits} className="btn-primary" data-testid="guest-save-btn">Save</button>
                <button onClick={() => { setEditing(false); setForm({ ...localGuest }); }} className="btn-ghost">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mb-8 text-sm" style={{ color: "var(--ink-2)" }}>
              {localGuest.email && <div className="flex items-center gap-2"><Mail size={13} style={{ color: "var(--muted)" }} /> {localGuest.email}</div>}
              {localGuest.phone && <div className="flex items-center gap-2"><Phone size={13} style={{ color: "var(--muted)" }} /> {localGuest.phone}</div>}
              {localGuest.address_city && <div className="flex items-center gap-2"><MapPin size={13} style={{ color: "var(--muted)" }} /> {localGuest.address_city}</div>}
              {localGuest.age_group && <div className="flex items-center gap-2"><Users size={13} style={{ color: "var(--muted)" }} /> {localGuest.age_group}</div>}
              {localGuest.referral_source && <div className="col-span-2 text-xs" style={{ color: "var(--muted)" }}>Heard about us via: <span style={{ color: "var(--ink)" }}>{localGuest.referral_source}</span></div>}
              {localGuest.invited_by && <div className="col-span-2 text-xs" style={{ color: "var(--muted)" }}>Invited by: <span style={{ color: "var(--ink)" }}>{localGuest.invited_by}</span></div>}
              {localGuest.notes && <div className="col-span-2 mt-2 p-3 rounded-md" style={{ background: "var(--surface-hover)", borderLeft: "3px solid var(--ember)" }}><p className="text-sm whitespace-pre-line" style={{ color: "var(--ink)" }}>{localGuest.notes}</p></div>}
            </div>
          )}

          {/* Teacher assignment — multi-teacher (husband/wife teams supported) */}
          <div className="mb-8 p-4 rounded-md border" style={{ borderColor: localGuest.stage === "ready_for_assignment" ? "var(--ember)" : "var(--border-subtle)", background: localGuest.stage === "ready_for_assignment" ? "var(--surface-hover)" : "var(--bg-alt)" }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 flex items-center justify-center rounded-md" style={{ width: 36, height: 36, background: "var(--bg-base)", color: "var(--ember)" }}>
                  <UserCheck size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
                    Home Study Teacher{(localGuest.assigned_teachers?.length || 0) > 1 ? "s" : ""}
                  </div>
                  {(localGuest.assigned_teachers?.length || 0) > 0 ? (
                    <div className="font-serif-display text-lg leading-tight" style={{ color: "var(--ink)" }} data-testid="assigned-teachers-names">
                      {localGuest.assigned_teachers.map((t) => t.name).join(" & ")}
                    </div>
                  ) : (
                    <div className="text-sm italic" style={{ color: "var(--muted)" }}>Not yet assigned</div>
                  )}
                </div>
              </div>
              <button onClick={() => setAssigning(!assigning)} className="btn-ghost" style={{ padding: "0.5rem 0.9rem" }} data-testid="assign-teacher-btn">
                {(localGuest.assigned_teachers?.length || 0) > 0 ? "Change" : "Assign"}
              </button>
            </div>
            {assigning && (
              <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: "var(--border-subtle)" }}>
                {/* Scope toggle */}
                {!user?.is_admin && (
                  <div className="flex items-center justify-between gap-2">
                    <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--surface-hover)" }} data-testid="assign-scope-tabs">
                      {[["mine", "My teachers"], ["all", "All in org"]].map(([k, l]) => (
                        <button
                          key={k}
                          onClick={() => setAssignScope(k)}
                          data-testid={`assign-scope-${k}`}
                          className="px-3 py-1 text-xs rounded-full transition-colors"
                          style={{ background: assignScope === k ? "var(--ink)" : "transparent", color: assignScope === k ? "#FFFFFF" : "var(--ink-2)", fontWeight: assignScope === k ? 600 : 400 }}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                    <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>
                      Tip: check multiple for husband/wife teams
                    </span>
                  </div>
                )}

                {visibleTeachers.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--muted)" }}>
                    {assignScope === "mine" && !user?.is_admin
                      ? "No teachers linked to you yet. Switch to “All in org” or assign yourself in the Teachers tab."
                      : "No active teachers yet. Add one in the Teachers tab."}
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="assign-teacher-list">
                    {visibleTeachers.map((t) => {
                      const selected = assignedIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          onClick={() => toggleTeacher(t.id)}
                          className="w-full text-left p-3 rounded-md border transition-colors flex items-start gap-3"
                          style={{ borderColor: selected ? "var(--ember)" : "var(--border-subtle)", background: selected ? "var(--surface-hover)" : "transparent" }}
                          data-testid={`assign-to-${t.id}`}
                        >
                          <div className="mt-0.5 shrink-0">
                            {selected
                              ? <CheckCircle2 size={18} style={{ color: "var(--ember)" }} />
                              : <Circle size={18} style={{ color: "var(--muted)" }} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{t.name}</div>
                            <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
                              {[t.city, t.age_group_pref, (t.interests || []).join(", ")].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {assignedIds.length > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "var(--border-subtle)" }}>
                    <span className="text-xs" style={{ color: "var(--muted)" }}>
                      {assignedIds.length} teacher{assignedIds.length === 1 ? "" : "s"} assigned
                    </span>
                    <button onClick={clearAll} className="text-xs underline" style={{ color: "var(--terracotta)" }} data-testid="unassign-teacher">
                      Remove all
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <details className="mb-4 rounded-lg group/d" style={{ border: "1px solid var(--border-subtle)" }} data-testid="profile-more-assignments">
            <summary className="cursor-pointer list-none flex items-center justify-between p-3 text-sm" style={{ color: "var(--ink-2)" }}>
              <span>Classes, attendance &amp; roles</span><ChevronRight size={16} className="transition-transform group-open/d:rotate-90" style={{ color: "var(--muted)" }} />
            </summary>
            <div className="px-3 pb-3"><AssignmentsPanel guestId={localGuest.id} orgId={orgId} onChanged={onSaved} email={localGuest.email} /></div>
          </details>

          {/* Progress tracks. Guest Connection is its own section (shared across all
              teachers). Phase 1 (First Steps) = Introduction + Home Study. Phase 2
              (Next Steps) lives on /crm/next-steps. */}
          <div className="pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            <details className="mb-6 rounded-lg group/c" style={{ border: "1px solid var(--border-subtle)" }} data-testid="section-guest-connection">
              <summary className="cursor-pointer list-none flex items-center justify-between p-3 text-sm" style={{ color: "var(--ink-2)" }}>
                <span>Guest connection log</span><ChevronRight size={16} className="transition-transform group-open/c:rotate-90" style={{ color: "var(--muted)" }} />
              </summary>
              <div className="px-3 pb-3"><ConnectionLog scope="coord" guestId={localGuest.id} orgId={orgId} /></div>
            </details>
            <section className="mb-6" data-testid="section-first-steps">
              <div className="mb-4 pb-2 border-b" style={{ borderColor: "var(--border-subtle)" }}>
                <div className="overline" style={{ color: "var(--terracotta)", fontSize: "0.6rem" }}>Phase 1</div>
                <h3 className="font-serif-display text-2xl mt-1" style={{ color: "var(--ink)" }}>First Steps</h3>
              </div>
              {FIRST_STEPS_TRACKS.map((t) => (
                <ProgressTrack key={t.key} track={t} sessions={localGuest[TRACK_FIELD[t.key]] || []} onToggle={toggleSession} resources={resources} defaultOpen={nextLesson(localGuest)?.track.key === t.key} />
              ))}
            </section>

            {/* Activity log — audit trail of every check-off and note from teachers & coordinators */}
            <section className="mb-6" data-testid="section-activity-log">
              <ActivityLogPanel scope="coord" guestId={localGuest.id} orgId={orgId} defaultOpen={false} />
            </section>

            {/* Graduate to Phase 2 */}
            <PhaseGraduationCard guest={localGuest} orgId={orgId} onChanged={onSaved} />

            {/* Church body status — manual labels */}
            <section className="mt-10 rounded-md p-5" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="guest-lifecycle-card">
              <h4 className="overline mb-1" style={{ color: "var(--gold)" }}>Church body status</h4>
              <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>
                Set the label manually — it stays where you put it. <strong>Guest</strong> = in First Steps · <strong>Disciple</strong> = in Next Steps · <strong>Member</strong> = in the church body / attendance. Adding to rosters or sections won't change this.
              </p>
              <div className="flex flex-wrap gap-2" data-testid="lifecycle-buttons">
                {[
                  { key: "guest",    label: "Guest",    hint: "First Steps" },
                  { key: "disciple", label: "Disciple", hint: "Next Steps" },
                  { key: "member",   label: "Member",   hint: "Attendance / body" },
                ].map((opt) => {
                  const active = (localGuest.lifecycle_status || "guest") === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setLifecycle(opt.key)}
                      disabled={changingLifecycle}
                      data-testid={`lifecycle-btn-${opt.key}`}
                      className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] transition-colors flex flex-col items-start gap-0.5"
                      style={{
                        background: active ? "var(--gold)" : "transparent",
                        color: active ? "#FFFFFF" : "var(--ink)",
                        border: `1px solid ${active ? "var(--gold)" : "var(--border-strong)"}`,
                        opacity: changingLifecycle ? 0.6 : 1,
                        minWidth: "9rem",
                      }}
                    >
                      <span className="font-semibold">{opt.label}</span>
                      <span className="text-[0.55rem] normal-case tracking-normal opacity-80">{opt.hint}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Danger zone — archive first, hard delete only if unlinked */}
            <section className="mt-10 rounded-md p-5" style={{ background: "rgba(191,90,66,0.04)", border: "1px solid rgba(191,90,66,0.2)" }} data-testid="guest-danger-zone">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle size={18} style={{ color: "var(--terracotta)" }} className="mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-medium text-sm" style={{ color: "var(--terracotta)" }}>Archive this profile</h4>
                  <p className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
                    Hides the person from active lists so your team doesn't get distracted, but their progress, notes, and history stay safe. You can restore them any time.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={archiveGuest}
                  disabled={archiving}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] font-medium hover:bg-[var(--terracotta)] hover:text-white transition-colors"
                  style={{ color: "var(--terracotta)", border: "1px solid var(--terracotta)", background: "transparent" }}
                  data-testid="guest-archive-btn"
                >
                  <Archive size={13} /> {archiving ? "Archiving…" : "Archive this profile"}
                </button>
                {!confirmingDelete ? (
                  <button
                    onClick={() => setConfirmingDelete(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] font-medium hover:bg-[var(--ink)] hover:text-white transition-colors"
                    style={{ color: "var(--muted)", border: "1px solid var(--border-strong)", background: "transparent" }}
                    data-testid="guest-delete-btn"
                    title="Permanent delete — blocked if this person is on any section or class roster"
                  >
                    <Trash2 size={13} /> Hard delete…
                  </button>
                ) : null}
              </div>
              {confirmingDelete && (
                <div className="mt-4 space-y-2 p-3" style={{ background: "var(--bg-alt)", borderLeft: "3px solid var(--terracotta)" }}>
                  <p className="text-xs" style={{ color: "var(--ink-2)" }}>
                    <strong style={{ color: "var(--terracotta)" }}>Warning:</strong> hard delete cannot be undone. If this person is on an attendance section or a Next Steps class, the delete will be refused — archive instead.
                  </p>
                  <label className="block text-[0.65rem]" style={{ color: "var(--ink-2)" }}>
                    Type <strong>{expectedConfirmText}</strong> to confirm:
                  </label>
                  <input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={expectedConfirmText}
                    className="w-full max-w-sm px-3 py-1.5 text-sm"
                    data-testid="guest-delete-confirm-input"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button onClick={deleteGuest} disabled={deleting || deleteConfirmText.trim().toLowerCase() !== expectedConfirmText.toLowerCase()} className="btn-primary text-xs" style={{ background: "var(--terracotta)", opacity: (deleting || deleteConfirmText.trim().toLowerCase() !== expectedConfirmText.toLowerCase()) ? 0.6 : 1 }} data-testid="guest-delete-confirm-btn">
                      {deleting ? "Deleting…" : "Permanently delete"}
                    </button>
                    <button onClick={() => { setConfirmingDelete(false); setDeleteConfirmText(""); }} className="btn-ghost text-xs" data-testid="guest-delete-cancel-btn">Cancel</button>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhaseGraduationCard({ guest, orgId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const orgParams = orgId ? { org_id: orgId } : {};

  const graduate = async () => {
    setBusy(true);
    try {
      await api.put(`/coord/guests/${guest.id}/graduate-to-next-steps`, null, { params: orgParams });
      toast.success("Enrolled in Next Steps.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't graduate.");
    } finally { setBusy(false); }
  };

  const undo = async () => {
    if (!window.confirm("Move this guest back out of Next Steps? Their Phase 2 progress will be preserved but they'll no longer appear on the Next Steps page.")) return;
    setBusy(true);
    try {
      await api.put(`/coord/guests/${guest.id}/undo-graduation`, null, { params: orgParams });
      toast.success("Moved back to Phase 1 only.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't undo.");
    } finally { setBusy(false); }
  };

  if (guest.phase2_enrolled) {
    return (
      <div className="p-4 rounded-md border-l-4 flex items-start gap-3 flex-wrap" style={{ background: "var(--surface-hover)", borderColor: "var(--gold)" }} data-testid="phase2-graduated-banner">
        <GraduationCap size={20} style={{ color: "var(--gold)" }} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--gold)" }}>Phase 2 · Next Steps</div>
          <p className="text-sm mt-1" style={{ color: "var(--ink)" }}>
            Enrolled{guest.phase2_enrolled_at ? ` on ${new Date(guest.phase2_enrolled_at).toLocaleDateString()}` : ""}. Open the Next Steps page to track the 24-lesson class.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => navigate("/crm/next-steps")} className="btn-ghost" style={{ padding: "0.5rem 0.9rem" }} data-testid="go-to-next-steps-btn">
            Open Next Steps <ArrowRight size={13} />
          </button>
          <button onClick={undo} disabled={busy} className="btn-ghost text-xs" style={{ padding: "0.5rem 0.9rem", color: "var(--terracotta)" }} data-testid="undo-graduation-btn" title="Move back to Phase 1 only">
            <Undo2 size={12} /> Undo
          </button>
        </div>
      </div>
    );
  }

  const ready = guest.phase1_complete;
  return (
    <div className="p-4 rounded-md border-l-4 flex items-start gap-3 flex-wrap" style={{ background: ready ? "var(--surface-hover)" : "var(--bg-alt)", borderColor: ready ? "var(--ember)" : "var(--border-subtle)" }} data-testid="phase2-graduation-cta">
      <GraduationCap size={20} style={{ color: ready ? "var(--ember)" : "var(--muted)" }} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: ready ? "var(--ember)" : "var(--muted)" }}>Phase 2 · Next Steps</div>
        <p className="text-sm mt-1" style={{ color: "var(--ink)" }}>
          {ready
            ? "First Steps complete. Ready to graduate this guest to the 24-lesson Next Steps class."
            : "Not yet enrolled. You can graduate anyone to Next Steps once you're ready — no need to wait for every Phase 1 session to be checked."}
        </p>
      </div>
      <button
        onClick={graduate}
        disabled={busy}
        className="btn-primary shrink-0"
        style={{ padding: "0.55rem 1rem" }}
        data-testid="graduate-to-next-steps-btn"
      >
        {busy ? "Enrolling…" : "Graduate to Next Steps"} <ArrowRight size={13} />
      </button>
    </div>
  );
}

// ==================== NEW GUEST MODAL ====================

// ==================== PRE-SERVICE TEXT MODAL ====================
// Splits active First Steps guests among the org's coordinators so we can
// manually send reminder texts before a service. Male coords get male guests,
// female coords get female guests; ungendered guests are split evenly across
// all coords. Every open (and every "Re-shuffle" tap) hits the backend so the
// same people aren't texted by the same coordinator every week.
function PreServiceTextModal({ orgId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    setErr("");
    api.get("/coord/pre-service-text", { params: { org_id: orgId } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't build the list."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [orgId]);

  const copyList = async (assignment) => {
    const header = `${formatDisplayName(assignment.coordinator)} — ${assignment.guests.length} people`;
    const lines = assignment.guests.map((g, i) => `${i + 1}. ${formatDisplayName(g)} — ${g.phone || "(no phone)"}`);
    const text = [header, ...lines].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${assignment.guests.length} names.`);
    } catch { toast.error("Couldn't copy."); }
  };

  const copyAll = async () => {
    if (!data) return;
    const blocks = data.assignments
      .filter((a) => a.guests.length > 0)
      .map((a) => {
        const header = `${formatDisplayName(a.coordinator)} — ${a.guests.length} ${a.guests.length === 1 ? "person" : "people"}`;
        const lines = a.guests.map((g, i) => `${i + 1}. ${formatDisplayName(g)} — ${g.phone || "(no phone)"}`);
        return [header, ...lines].join("\n");
      });
    if (data.unassigned.length > 0) {
      const lines = data.unassigned.map((g, i) => `${i + 1}. ${formatDisplayName(g)} — ${g.phone || "(no phone)"}`);
      blocks.push(["Unassigned — " + data.unassigned.length + " " + (data.unassigned.length === 1 ? "person" : "people"), ...lines].join("\n"));
    }
    const totalPeople = data.assignments.reduce((s, a) => s + a.guests.length, 0) + data.unassigned.length;
    if (blocks.length === 0) { toast.info("Nothing to copy."); return; }
    const text = blocks.join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${totalPeople} ${totalPeople === 1 ? "name" : "names"} across ${blocks.length} ${blocks.length === 1 ? "group" : "groups"}.`);
    } catch { toast.error("Couldn't copy."); }
  };

  const totals = data?.totals;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.55)" }} onClick={onClose} data-testid="pre-service-overlay">
      <div className="w-full max-w-3xl rounded-md relative" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} onClick={(e) => e.stopPropagation()} data-testid="pre-service-modal">
        {/* Header */}
        <div className="p-6 border-b flex items-start justify-between gap-4 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="min-w-0">
            <div className="overline mb-1" style={{ color: "var(--terracotta)", fontSize: "0.6rem" }}>Pre-service reminder texts</div>
            <h2 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>Who's texting whom</h2>
            <p className="text-xs mt-2 max-w-xl" style={{ color: "var(--muted)" }}>
              Active First Steps guests with a phone, split among your coordinators. Male coords get male guests, female coords get female guests; unassigned people are split evenly. Re-shuffle any time — every draw is randomized.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {data && (data.assignments.some((a) => a.guests.length > 0) || data.unassigned.length > 0) && (
              <button onClick={copyAll} className="btn-ghost" data-testid="pre-service-copy-all" title="Copy every group as one block for pasting elsewhere">
                <Copy size={14} /> Copy all
              </button>
            )}
            <button onClick={load} className="btn-ghost" data-testid="pre-service-reshuffle" title="Randomize and redraw the list">
              <Shuffle size={14} /> Re-shuffle
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid="pre-service-close" aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {loading ? (
            <p className="text-sm italic" style={{ color: "var(--muted)" }} data-testid="pre-service-loading">Building the list…</p>
          ) : err ? (
            <p className="text-sm" style={{ color: "var(--terracotta)" }} data-testid="pre-service-error">{err}</p>
          ) : !data ? null : (
            <>
              {/* Totals strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="pre-service-totals">
                <TotalPill label="Guests w/ phone" value={totals.guests} />
                <TotalPill label="Male" value={totals.males} />
                <TotalPill label="Female" value={totals.females} />
                <TotalPill label="Coordinators" value={totals.coordinators} />
              </div>

              {totals.coordinators === 0 ? (
                <div className="p-4 rounded-md text-sm" style={{ background: "var(--surface-hover)", border: "1px solid var(--border-subtle)", color: "var(--ink-2)" }} data-testid="pre-service-no-coords">
                  <strong style={{ color: "var(--ink)" }}>No coordinators yet.</strong> Add someone as a coordinator (Teachers tab → toggle "Coordinator") so we know who to split the list among.
                </div>
              ) : data.assignments.every((a) => a.guests.length === 0) && data.unassigned.length === 0 ? (
                <div className="p-4 rounded-md text-sm" style={{ background: "var(--surface-hover)", border: "1px solid var(--border-subtle)", color: "var(--ink-2)" }} data-testid="pre-service-empty">
                  No active First Steps guests with a phone number yet. Add phone numbers to the profiles you want to text.
                </div>
              ) : (
                <div className="space-y-5">
                  {data.assignments.map((a) => (
                    <div key={a.coordinator.id} className="rounded-md border" style={{ borderColor: "var(--border-subtle)" }} data-testid={`pre-service-coord-${a.coordinator.id}`}>
                      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b" style={{ borderColor: "var(--border-subtle)", background: "var(--surface-hover)" }}>
                        <div className="min-w-0">
                          <div className="font-medium truncate" style={{ color: "var(--ink)" }} data-testid={`pre-service-coord-name-${a.coordinator.id}`}>
                            {formatDisplayName(a.coordinator)}
                          </div>
                          <div className="text-[0.65rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
                            {a.guests.length} {a.guests.length === 1 ? "person" : "people"}
                          </div>
                        </div>
                        {a.guests.length > 0 && (
                          <button onClick={() => copyList(a)} className="btn-ghost" data-testid={`pre-service-copy-${a.coordinator.id}`} title="Copy this list to paste into your text app">
                            <Copy size={12} /> Copy list
                          </button>
                        )}
                      </div>
                      {a.guests.length === 0 ? (
                        <p className="text-xs italic px-4 py-3" style={{ color: "var(--muted)" }}>No one on their list this round.</p>
                      ) : (
                        <ol className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
                          {a.guests.map((g, i) => (
                            <li key={g.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm" data-testid={`pre-service-row-${a.coordinator.id}-${g.id}`}>
                              <span className="min-w-0 truncate" style={{ color: "var(--ink)" }}>
                                <span className="mr-2 text-[0.65rem]" style={{ color: "var(--muted)" }}>{i + 1}.</span>
                                {formatDisplayName(g)}
                              </span>
                              <a href={`tel:${g.phone}`} className="text-xs whitespace-nowrap" style={{ color: "var(--terracotta)" }} data-testid={`pre-service-phone-${g.id}`}>{g.phone}</a>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  ))}

                  {data.unassigned.length > 0 && (
                    <div className="rounded-md border" style={{ borderColor: "var(--border-subtle)" }} data-testid="pre-service-unassigned-block">
                      <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border-subtle)", background: "var(--surface-hover)" }}>
                        <div className="font-medium" style={{ color: "var(--ink)" }}>Unassigned</div>
                        <div className="text-[0.65rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
                          {data.unassigned.length} {data.unassigned.length === 1 ? "person" : "people"} — grab as needed
                        </div>
                      </div>
                      <ol className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
                        {data.unassigned.map((g, i) => (
                          <li key={g.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm" data-testid={`pre-service-unassigned-${g.id}`}>
                            <span className="min-w-0 truncate" style={{ color: "var(--ink)" }}>
                              <span className="mr-2 text-[0.65rem]" style={{ color: "var(--muted)" }}>{i + 1}.</span>
                              {formatDisplayName(g)}
                            </span>
                            <a href={`tel:${g.phone}`} className="text-xs whitespace-nowrap" style={{ color: "var(--terracotta)" }}>{g.phone}</a>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TotalPill({ label, value }) {
  return (
    <div className="rounded-md px-3 py-2" style={{ background: "var(--surface-hover)", border: "1px solid var(--border-subtle)" }}>
      <div className="text-[0.55rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>{label}</div>
      <div className="font-serif-display text-xl leading-none mt-0.5" style={{ color: "var(--ink)" }}>{value}</div>
    </div>
  );
}


// ==================== SIGN-UP QR MODAL ====================
// Reuses the org's permanent QR code (same code used for /checkin) but embeds
// it in a /tracker-signup/<code> URL. Coord/teacher shows the QR to a guest so
// they can add themselves to the CRM without occupying anyone's phone.
function SignupQrModal({ orgId, onClose }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.get("/att/qr-code", { params: { org_id: orgId } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load the QR code."));
  }, [orgId]);
  const publicUrl = data ? `${window.location.origin}/tracker-signup/${data.code}` : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy."); }
  };
  const print = () => window.print();
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4 print:static print:py-0 print:px-0"
      style={{ background: "rgba(14,34,64,0.55)" }}
      onClick={onClose}
      data-testid="signup-qr-overlay"
    >
      <style>{`@media print { body * { visibility: hidden; } .qr-print, .qr-print * { visibility: visible; } .qr-print { position: absolute; left: 0; top: 0; width: 100%; } .qr-no-print { display: none !important; } }`}</style>
      <div
        className="w-full max-w-md rounded-md relative qr-print"
        style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start justify-between gap-3 qr-no-print">
            <div>
              <div className="overline mb-1" style={{ color: "var(--terracotta)", fontSize: "0.6rem" }}>Guest sign-up QR</div>
              <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>{data?.org_name || "\u2026"}</h3>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid="signup-qr-close" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="mt-6 flex flex-col items-center text-center">
            <div className="p-6 rounded" style={{ background: "#FFFFFF" }} data-testid="signup-qr-code">
              {publicUrl ? <QRCodeSVG value={publicUrl} size={220} level="M" includeMargin={false} /> : <div style={{ width: 220, height: 220 }} />}
            </div>
            <div className="mt-4 font-serif-display text-xl" style={{ color: "var(--ink)" }}>Scan to sign up</div>
            <div className="mt-1 text-sm max-w-xs" style={{ color: "var(--muted)" }}>
              Hand your phone to a first-time guest, or print this and post it near the welcome desk. Their sign-up drops straight into First Steps.
            </div>
            <div className="mt-4 text-[0.65rem] px-3 py-1.5 rounded font-mono break-all" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }} data-testid="signup-qr-url">
              {publicUrl || "\u2026"}
            </div>
          </div>

          <div className="mt-6 flex gap-2 flex-wrap qr-no-print">
            <button onClick={copy} disabled={!publicUrl} className="btn-ghost" data-testid="signup-qr-copy">
              <Copy size={13} /> {copied ? "Copied" : "Copy link"}
            </button>
            <button onClick={print} disabled={!publicUrl} className="btn-primary" data-testid="signup-qr-print">
              <Printer size={13} /> Print poster
            </button>
          </div>
          <p className="mt-4 text-[0.65rem] qr-no-print" style={{ color: "var(--muted)" }}>
            One QR per church. Unlike the attendance QR, this works any day, any time — no service window required.
          </p>
        </div>
      </div>
    </div>
  );
}



function RowTeacherAssign({ guest, teachers, orgId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  const activeTeachers = useMemo(() => teachers.filter((t) => t.active), [teachers]);
  const validIds = useMemo(() => new Set(activeTeachers.map((t) => t.id)), [activeTeachers]);
  const assignedIds = useMemo(() => {
    const raw = guest.assigned_teacher_ids || (guest.assigned_teachers || []).map((t) => t.id) || [];
    return raw.filter((id) => validIds.has(id));
  }, [guest, validIds]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const save = async (nextIds) => {
    const clean = Array.from(new Set(nextIds.filter((id) => validIds.has(id))));
    setBusy(true);
    try {
      const r = await api.put(`/coord/guests/${guest.id}/assign-teachers`, { teacher_ids: clean }, { params: { org_id: orgId } });
      const dropped = (r?.data?.dropped_ids || []).length;
      if (dropped > 0) toast.warning(`${dropped} stale teacher assignment${dropped === 1 ? "" : "s"} cleaned up.`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update teachers.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (tid) => {
    const s = new Set(assignedIds);
    if (s.has(tid)) s.delete(tid); else s.add(tid);
    save([...s]);
  };

  const label = assignedIds.length === 0
    ? "Assign teacher"
    : `${assignedIds.length} teacher${assignedIds.length === 1 ? "" : "s"}`;

  return (
    <div ref={wrapRef} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        data-testid={`row-assign-btn-${guest.id}`}
        className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-[0.14em] px-2 py-1 rounded-full border transition-colors disabled:opacity-50"
        style={{
          borderColor: assignedIds.length ? "var(--olive)" : "var(--ember)",
          color: assignedIds.length ? "var(--olive)" : "var(--ember)",
          background: "var(--bg-alt)",
        }}
      >
        <UserCheck size={11} /> {label}
      </button>
      {open && (
        <div
          className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-40 w-64 max-w-[80vw] rounded-md shadow-lg py-2 max-h-72 overflow-y-auto"
          style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}
          data-testid={`row-assign-popover-${guest.id}`}
        >
          <div className="px-3 pb-1.5 pt-0.5 text-[0.55rem] uppercase tracking-[0.18em] flex items-center justify-between" style={{ color: "var(--muted)" }}>
            <span>Assign teachers</span>
            {assignedIds.length > 0 && (
              <button onClick={() => save([])} className="underline" style={{ color: "var(--terracotta)" }} data-testid={`row-assign-clear-${guest.id}`}>
                Clear
              </button>
            )}
          </div>
          {activeTeachers.length === 0 ? (
            <div className="px-3 py-2 text-xs" style={{ color: "var(--muted)" }}>
              No teachers in this org yet. Add one from the Teachers tab.
            </div>
          ) : (
            activeTeachers.map((t) => {
              const on = assignedIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => toggle(t.id)}
                  disabled={busy}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-[var(--surface-hover)]"
                  data-testid={`row-assign-toggle-${guest.id}-${t.id}`}
                  style={{ color: "var(--ink)" }}
                >
                  {on ? <CheckCircle2 size={14} style={{ color: "var(--ember)" }} /> : <Circle size={14} style={{ color: "var(--muted)" }} />}
                  <span className="truncate">{t.name}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const QUIET_DAYS = 7;
const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(`${d.slice(0, 10)}T12:00:00`).getTime()) / 86400000) : null);
const isQuiet = (g) => g.stage !== "completed" && (daysSince(g.last_contact_at) ?? 999) >= QUIET_DAYS;

function nextLesson(g) {
  for (const t of FIRST_STEPS_TRACKS) {
    const sessions = g[TRACK_FIELD[t.key]] || [];
    const required = t.requiredCount ?? t.count;
    if (sessions.filter((x) => x?.completed).length >= required) continue;
    const index = Array.from({ length: t.count }).findIndex((_, i) => !sessions[i]?.completed);
    if (index >= 0) return { track: t, index, title: required < t.count ? t.label : sessionTitle(t.key, index) };
  }
  return null;
}

function GuestRow({ g, teachers, orgId, onOpen, onSaved, texts }) {
  const [busy, setBusy] = useState(false);
  const stage = STAGE_META[g.stage] || STAGE_META.new;
  const done = FIRST_STEPS_TRACKS.reduce((s, t) => s + Math.min(g.progress?.[t.key]?.done || 0, t.requiredCount ?? t.count), 0);
  const next = nextLesson(g);
  const [choose, setChoose] = useState(false);
  const eitherOr = next && (next.track.requiredCount ?? next.track.count) < next.track.count;
  const save = async (index) => {
    setBusy(true); setChoose(false);
    try {
      await api.put(`/coord/guests/${g.id}/progress`, { track: next.track.key, index, completed: true, date: todayIso(), notes: "" }, { params: { org_id: orgId } });
      toast.success(`Marked "${sessionTitle(next.track.key, index)}" done.`);
      onSaved();
    } catch { toast.error("Couldn't save. Try again."); } finally { setBusy(false); }
  };
  const markDone = (e) => {
    e.stopPropagation();
    if (eitherOr) setChoose((c) => !c);
    else save(next.index);
  };
  return (
    <div onClick={onOpen} className="group card-surface relative focus-within:z-30 px-4 py-3 cursor-pointer flex items-center gap-3 sm:gap-4 transition-colors" data-testid={`guest-row-${g.id}`}>
      <ProgressRing value={done} total={FIRST_STEPS_TOTAL} size={38} stroke={3} color={stage.color} label={`${done} of ${FIRST_STEPS_TOTAL} complete`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-serif-display text-base leading-tight truncate" style={{ color: "var(--ink)" }}>{formatDisplayName(g)}</h3>
          <span className="px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.16em] rounded-full" style={{ background: `color-mix(in srgb, ${stage.color} 18%, transparent)`, color: stage.color }}>{stage.label}</span>
        </div>
        <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs" style={{ color: "var(--muted)" }}>
          {(g.assigned_teachers || []).length > 0
            ? <span data-testid={`guest-row-teachers-${g.id}`}>{g.assigned_teachers.map((t) => t.name).join(" & ")}</span>
            : <span onClick={(e) => e.stopPropagation()}><RowTeacherAssign guest={g} teachers={teachers} orgId={orgId} onSaved={onSaved} /></span>}
          {next && <span className="truncate" data-testid={`guest-row-next-${g.id}`}>Next: <span style={{ color: "var(--ink-2)" }}>{next.title}</span></span>}
          {!next && <span style={{ color: "var(--olive)" }}>First Steps complete</span>}
          {(() => { const d = daysSince(g.last_contact_at) ?? 999; if (d < QUIET_DAYS || g.stage === "completed") return null; return <span style={{ color: "#F87171" }} data-testid={`guest-row-quiet-${g.id}`}>Last contact {d >= 999 ? "never" : `${d} days ago`}</span>; })()}
        </div>
      </div>
      <TextGuestButton guest={g} orgId={orgId} examples={texts?.examples} churchName={texts?.church_name} onLogged={onSaved} />
      {next && (
        <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
        {choose && (
          <div className="absolute right-0 bottom-full mb-2 z-40 w-60 rounded-md shadow-xl py-1" style={{ background: "var(--bg-deep)", border: "1px solid var(--border-strong)" }} data-testid={`guest-row-choose-${g.id}`}>
            <div className="px-3 py-1.5 text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Which lesson was taught?</div>
            {Array.from({ length: next.track.count }).map((_, i) => (
              <button key={i} onClick={() => save(i)} className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface-hover)]" style={{ color: "var(--ink)" }} data-testid={`guest-row-choose-${g.id}-${i}`}>{sessionTitle(next.track.key, i)}</button>
            ))}
          </div>
        )}
        <button onClick={markDone} disabled={busy} className="shrink-0 h-9 px-3 rounded-full flex items-center gap-1.5 text-xs transition-colors text-[var(--olive)] hover:bg-[var(--olive)] hover:text-[var(--bg-base)]" style={{ border: "1px solid var(--olive)" }} title={eitherOr ? "Choose which lesson was taught" : `Mark "${next.title}" done today`} data-testid={`guest-row-markdone-${g.id}`}>
          <Check size={14} /> <span className="hidden sm:inline">{busy ? "Saving…" : "Done"}</span>
        </button>
        </div>
      )}
      <ChevronRight size={16} className="shrink-0 hidden sm:block transition-transform group-hover:translate-x-0.5" style={{ color: "var(--muted)" }} />
    </div>
  );
}

// ==================== GUESTS PANEL ====================
function GuestsPanel({ orgId }) {
  const [guests, setGuests] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [preServiceOpen, setPreServiceOpen] = useState(false);
  const [textsOpen, setTextsOpen] = useState(false);
  const [texts, setTexts] = useState(null);
  useEffect(() => {
    if (!orgId) return;
    api.get("/coord/text-examples", { params: { org_id: orgId } }).then((r) => setTexts(r.data)).catch(() => setTexts(null));
  }, [orgId]);

  const load = () => {
    if (!orgId) return;
    setLoading(true);
    const params = { org_id: orgId };
    Promise.all([
      api.get("/coord/guests", { params }),
      api.get("/coord/teachers", { params }),
    ])
      .then(([g, t]) => { setGuests(g.data); setTeachers(t.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [orgId]);

  // Helper: a guest "needs a teacher" if they haven't had one assigned to Phase 1
  // yet. This is the coordinator's mental model — every guest without an
  // assigned teacher shows up here, regardless of Introduction progress.
  const hasNoTeacher = (g) =>
    !((g.assigned_teacher_ids && g.assigned_teacher_ids.length) || g.assigned_teacher_id);

  const filtered = useMemo(() => {
    // Only actual "guest" lifecycle rows belong on First Steps — disciples are in
    // Next Steps, members are church family already (they belong in the directory /
    // attendance / prayer tools, not the evangelism queue). Once graduated to
    // phase 2 they show up on the Next Steps "pending class" queue instead.
    let list = guests.filter((g) => (g.lifecycle_status || "guest") === "guest" && !g.phase2_enrolled);
    if (stageFilter === "quiet") {
      list = list.filter(isQuiet).sort((a, b) => (a.last_contact_at || "").localeCompare(b.last_contact_at || ""));
    } else if (stageFilter === "in_progress") {
      list = list.filter((g) => !hasNoTeacher(g) && g.stage !== "completed");
    } else if (stageFilter === "ready_for_assignment") {
      // "Needs teacher" filter — widened to match the "ASSIGN TEACHER" pills on
      // the guest rows (any active guest without a teacher, not just those who
      // completed Introduction).
      list = list.filter(hasNoTeacher);
    } else if (stageFilter !== "all") {
      list = list.filter((g) => g.stage === stageFilter);
    }
    if (ageFilter !== "all") {
      list = list.filter((g) => ageFilter === "unset" ? !g.age_group : g.age_group === ageFilter);
    }
    if (q.trim()) {
      const l = q.toLowerCase();
      list = list.filter((g) =>
        `${g.first_name} ${g.last_name}`.toLowerCase().includes(l) ||
        (g.email || "").toLowerCase().includes(l) ||
        (g.phone || "").includes(l) ||
        (g.address_city || "").toLowerCase().includes(l)
      );
    }
    return list;
  }, [guests, q, stageFilter, ageFilter]);

  const activeGuests = useMemo(() => guests.filter((g) => (g.lifecycle_status || "guest") === "guest" && !g.phase2_enrolled), [guests]);
  const counts = useMemo(() => {
    const c = { all: activeGuests.length };
    STAGES.forEach((s) => { c[s.key] = activeGuests.filter((g) => g.stage === s.key).length; });
    // Override "ready_for_assignment" to match the "no teacher assigned" mental
    // model shared with the stat card / callout / row pills.
    c.ready_for_assignment = activeGuests.filter(hasNoTeacher).length;
    return c;
  }, [activeGuests]);

  // Age-group counts — used for the age filter chips. Only groups with at least
  // one active guest get a chip so we don't spray every possible label.
  const ageCounts = useMemo(() => {
    const c = { all: activeGuests.length, unset: 0 };
    AGE_GROUPS.forEach((a) => { c[a] = 0; });
    activeGuests.forEach((g) => {
      if (!g.age_group) c.unset += 1;
      else if (c[g.age_group] !== undefined) c[g.age_group] += 1;
    });
    return c;
  }, [activeGuests]);
  const visibleAgeGroups = useMemo(() => AGE_GROUPS.filter((a) => ageCounts[a] > 0), [ageCounts]);

  const stats = useMemo(() => {
    const total = activeGuests.length;
    const active = activeGuests.filter((g) => !hasNoTeacher(g) && g.stage !== "completed").length;
    const needsTeacher = counts.ready_for_assignment || 0;
    const completed = counts.completed || 0;
    const graduated = guests.filter((g) => g.phase2_enrolled).length;
    return { total, active, needsTeacher, completed, graduated };
  }, [guests, activeGuests, counts]);

  const selected = guests.find((g) => g.id === selectedId);

  const tab = { ready_for_assignment: "needs", completed: "completed", in_progress: "progress", quiet: "quiet", all: "all" }[stageFilter] || "";
  const tabs = [
    { key: "needs", label: "Needs teacher", count: stats.needsTeacher, color: "var(--ember)" },
    { key: "progress", label: "In progress", count: stats.active },
    { key: "quiet", label: "Quiet 7+ days", count: activeGuests.filter(isQuiet).length, color: "#F87171" },
    { key: "completed", label: "Completed", count: stats.completed, color: "var(--olive)" },
    { key: "all", label: "All", count: counts.all },
  ];
  const setTab = (k) => setStageFilter({ needs: "ready_for_assignment", progress: "in_progress", quiet: "quiet", completed: "completed", all: "all" }[k]);
  const filterGroups = [
    { key: "stage", label: "Stage", value: ["all", "in_progress"].includes(stageFilter) ? "all" : stageFilter, onChange: setStageFilter, options: [{ value: "all", label: "Any stage", count: counts.all }, ...STAGES.filter((s) => !["ready_for_assignment", "completed"].includes(s.key)).map((s) => ({ value: s.key, label: s.label, count: counts[s.key] || 0 }))] },
    ...(ageCounts.unset < activeGuests.length ? [{ key: "age", label: "Age group", value: ageFilter, onChange: setAgeFilter, options: [{ value: "all", label: "Any age", count: ageCounts.all }, ...visibleAgeGroups.map((a) => ({ value: a, label: a, count: ageCounts[a] })), ...(ageCounts.unset ? [{ value: "unset", label: "Not set", count: ageCounts.unset }] : [])] }] : []),
  ];
  const filterCount = (stageFilter !== "all" && !["ready_for_assignment", "completed", "in_progress", "quiet"].includes(stageFilter) ? 1 : 0) + (ageFilter !== "all" ? 1 : 0);

  return (
    <div data-testid="guests-panel">
      <div className="lg:sticky lg:top-[73px] z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 mb-3 space-y-3" style={{ background: "color-mix(in srgb, var(--bg-base) 92%, transparent)", backdropFilter: "blur(8px)" }}>
        <TrackerToolbar
          testidPrefix="fs"
          searchTestid="guest-search"
          search={q}
          onSearch={setQ}
          placeholder="Search guests…"
          filters={filterGroups}
          filterCount={filterCount}
          more={[
            { label: "Sign-up QR", icon: QrCode, onClick: () => setQrOpen(true), testid: "show-signup-qr-btn" },
            { label: "Pre-service text", icon: MessageSquare, onClick: () => setPreServiceOpen(true), testid: "open-pre-service-text-btn" },
            { label: "Text examples", icon: FileText, onClick: () => setTextsOpen(true), testid: "open-text-examples-btn" },
          ]}
          primary={<button onClick={() => setNewOpen(true)} className="btn-primary h-10 shrink-0" style={{ padding: "0 1.1rem" }} data-testid="add-guest-btn"><UserPlus size={15} /> <span className="hidden sm:inline">Add guest</span></button>}
        />
        <TabBar tabs={tabs} value={tab} onChange={setTab} testidPrefix="fs-tab" />
      </div>

      {loading ? <p style={{ color: "var(--muted)" }}>Loading…</p> :
        filtered.length === 0 ? (
          guests.length === 0 ? (
            <EmptyState icon={Users} title="No guests yet" description="Add your first guest to start tracking their journey." action={<button onClick={() => setNewOpen(true)} className="btn-primary" data-testid="empty-add-guest-btn"><UserPlus size={14} /> Add your first guest</button>} testid="guests-empty-state" />
          ) : (
            <EmptyState icon={Search} title="Nobody here" description={q.trim() ? "No guests match your search." : tab === "needs" ? "Every guest has a teacher. Nice work." : tab === "quiet" ? "Everyone has been contacted in the last week. Great job." : "Try another tab or clear the search."} testid="guests-no-results" />
          )
        ) : (
          <div className="space-y-2">
            {filtered.map((g) => <GuestRow key={g.id} g={g} teachers={teachers} orgId={orgId} texts={texts} onOpen={() => setSelectedId(g.id)} onSaved={load} />)}
          </div>
        )
      }

      <SummaryBar items={[
        { label: "Total guests", value: stats.total, testid: "coord-stat-total", onClick: () => setTab("all") },
        { label: "Active", value: stats.active, color: "var(--terracotta)", testid: "coord-stat-active", onClick: () => setTab("progress") },
        { label: "Needs teacher", value: stats.needsTeacher, color: "var(--ember)", testid: "coord-stat-needs", onClick: () => setTab("needs") },
        { label: "Completed", value: stats.completed, color: "var(--olive)", testid: "coord-stat-completed", onClick: () => setTab("completed") },
      ]} />

      {selected && <GuestProfile guest={selected} teachers={teachers} orgId={orgId} onClose={() => setSelectedId(null)} onSaved={load} />}
      {newOpen && (
        <DirectoryPicker
          orgId={orgId}
          title="Add a guest to First Steps"
          subtitle="Pick someone from the directory. Their category becomes Guest so they show up in First Steps."
          actionLabel="Add to First Steps"
          defaultLifecycle="guest"
          testid="fs-picker"
          onClose={() => setNewOpen(false)}
          onPick={async (p) => {
            await api.put(`/coord/guests/${p.id}/lifecycle`, { status: "guest" }, { params: { org_id: orgId } });
            toast.success(`${formatDisplayName(p)} added to First Steps.`);
            setNewOpen(false);
            load();
          }}
        />
      )}
      {qrOpen && <SignupQrModal orgId={orgId} onClose={() => setQrOpen(false)} />}
      {preServiceOpen && <PreServiceTextModal orgId={orgId} onClose={() => setPreServiceOpen(false)} />}
      {textsOpen && <TextExamplesModal orgId={orgId} onChanged={setTexts} onClose={() => setTextsOpen(false)} />}
    </div>
  );
}

// ==================== TEACHERS PANEL ====================
function TeacherModal({ teacher, orgId, coordinators = [], onClose, onSaved }) {
  const initialCoordIds = teacher
    ? Array.from(new Set([...(teacher.coordinator_ids || []), ...(teacher.coordinator_id ? [teacher.coordinator_id] : [])]))
    : [];
  const [form, setForm] = useState(teacher ? { ...teacher, password: "", coordinator_ids: initialCoordIds } : { ...emptyTeacher(), coordinator_ids: [] });
  const [interestInput, setInterestInput] = useState("");
  const [busy, setBusy] = useState(false);
  const isEdit = !!teacher?.id;

  const addInterest = () => {
    const v = interestInput.trim();
    if (!v || form.interests.includes(v)) return;
    setForm({ ...form, interests: [...form.interests, v] });
    setInterestInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    if (payload.password && payload.password.length < 8) {
      return toast.error("Password must be at least 8 characters.");
    }
    if (payload.password && !payload.email) {
      return toast.error("Email is required to grant portal access.");
    }
    // Multi-coord: pass coordinator_ids; legacy coordinator_id is set server-side to ids[0].
    delete payload.coordinator_id;
    setBusy(true);
    try {
      const params = orgId ? { org_id: orgId } : {};
      if (isEdit) await api.put(`/coord/teachers/${teacher.id}`, payload, { params });
      else await api.post("/coord/teachers", payload, { params });
      const sentEmail = !!payload.password;
      const passwordReset = isEdit && sentEmail;
      const created = !isEdit && sentEmail;
      if (created)      toast.success(`Teacher added. Welcome email sent to ${payload.email}.`);
      else if (passwordReset) toast.success(`Password reset. Email sent to ${payload.email}.`);
      else              toast.success(isEdit ? "Teacher updated." : "Teacher added.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-xl card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="teacher-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-6" style={{ color: "var(--ink)" }}>{isEdit ? "Edit teacher" : "Add a teacher"}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[["name", "Name *", true], ["email", "Email", false, "email"], ["phone", "Phone"], ["city", "City / Area"]].map(([k, label, req, type]) => (
            <div key={k}>
              <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>{label}</label>
              <input required={req} type={type || "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid={`teacher-${k}`} />
            </div>
          ))}
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Age group preference</label>
            <select value={form.age_group_pref || ""} onChange={(e) => setForm({ ...form, age_group_pref: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="teacher-age-group">
              <option value="">—</option>
              {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Active</label>
            <select value={form.active ? "yes" : "no"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })} className="w-full px-3 py-2 text-sm">
              <option value="yes">Active</option>
              <option value="no">Inactive</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Managed by (coordinators)</label>
            <div className="rounded-md p-3 space-y-1.5" style={{ background: "var(--bg-deep-alt)", border: "1px solid var(--border-subtle)" }} data-testid="teacher-coord-multi">
              <label className="flex items-center gap-2 text-xs" style={{ color: "var(--ink)" }}>
                <input
                  type="checkbox"
                  checked={coordinators.length > 0 && form.coordinator_ids?.length === coordinators.length}
                  onChange={(e) => setForm({ ...form, coordinator_ids: e.target.checked ? coordinators.map((c) => c.id) : [] })}
                  className="h-4 w-4"
                  style={{ accentColor: "var(--ember)" }}
                  data-testid="teacher-coord-all"
                />
                <span className="uppercase tracking-[0.14em] text-[0.65rem]" style={{ color: "var(--ember)" }}>All coordinators</span>
              </label>
              <div className="border-t pt-2" style={{ borderColor: "var(--border-subtle)" }} />
              {coordinators.length === 0 && <p className="text-[0.7rem] italic" style={{ color: "var(--muted)" }}>No coordinators in this org yet.</p>}
              {coordinators.map((c) => {
                const checked = (form.coordinator_ids || []).includes(c.id);
                return (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--ink)" }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const cur = new Set(form.coordinator_ids || []);
                        if (e.target.checked) cur.add(c.id); else cur.delete(c.id);
                        setForm({ ...form, coordinator_ids: Array.from(cur) });
                      }}
                      className="h-4 w-4"
                      style={{ accentColor: "var(--ember)" }}
                      data-testid={`teacher-coord-${c.id}`}
                    />
                    <span>{c.name || c.email}{c.is_admin ? " (admin)" : ""}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-[0.65rem] mt-1" style={{ color: "var(--muted)" }}>
              A teacher can report to multiple coordinators — each of them will see the teacher's pending assignment requests.
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Interests / topics</label>
            <div className="flex gap-2">
              <input value={interestInput} onChange={(e) => setInterestInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInterest(); } }} placeholder="e.g. young couples, singles, families with kids…" className="flex-1 px-3 py-2 text-sm" data-testid="teacher-interest-input" />
              <button type="button" onClick={addInterest} className="btn-ghost" style={{ padding: "0.5rem 0.9rem" }}><Plus size={14} /></button>
            </div>
            {form.interests.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {form.interests.map((it, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }}>
                    {it}
                    <button type="button" onClick={() => setForm({ ...form, interests: form.interests.filter((_, j) => j !== i) })}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Notes</label>
            <textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="teacher-notes" />
          </div>

          <div className="md:col-span-2 border-t pt-4 mt-2" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="overline mb-1" style={{ fontSize: "0.55rem" }}>Portal access (optional)</div>
            {teacher?.user_id ? (
              <p className="text-xs mb-2" style={{ color: "var(--olive)" }}>
                ✓ This teacher has a portal login. Type a new password below to reset it (they'll get an email), or leave blank to keep it.
              </p>
            ) : (
              <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>
                Fill in email + password to give this teacher a login at <strong style={{ color: "var(--ink-2)" }}>/teacher/login</strong>. They'll receive a welcome email with these credentials and can then see and edit only their assigned guests.
              </p>
            )}
            <div>
              <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>
                {teacher?.user_id ? "Reset password (leave blank to keep)" : "Set password (min 8 chars)"}
              </label>
              <input
                type="text"
                value={form.password || ""}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={teacher?.user_id ? "••••••••" : "e.g. bibleStudy2026"}
                className="w-full px-3 py-2 text-sm"
                data-testid="teacher-password"
              />
            </div>
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="teacher-submit">{busy ? "Saving…" : (isEdit ? "Save changes" : "Add teacher")}</button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function TeachersPanel({ orgId }) {
  const { user } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [tq, setTq] = useState("");
  const [mergeOpen, setMergeOpen] = useState(false);
  const [scope, setScope] = useState(user?.is_admin ? "all" : "mine");
  const [requests, setRequests] = useState([]);
  const [pendingDecision, setPendingDecision] = useState(null); // { req, action }
  const [decisionNote, setDecisionNote] = useState("");
  const [decisionBusy, setDecisionBusy] = useState(false);
  const canScope = !user?.is_admin;
  const canMerge = !!(user?.is_admin || user?.is_org_admin);

  const load = () => {
    if (!orgId) return;
    setLoading(true);
    const params = { org_id: orgId };
    Promise.all([
      api.get("/coord/teachers", { params }),
      api.get("/coord/coordinators", { params }).catch(() => ({ data: [] })),
      api.get("/coord/assignment-requests", { params }).catch(() => ({ data: [] })),
    ])
      .then(([t, c, r]) => { setTeachers(t.data); setCoordinators(c.data || []); setRequests(r.data || []); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [orgId]);

  const coordinatorsById = useMemo(() => Object.fromEntries(coordinators.map((c) => [c.id, c])), [coordinators]);

  const decide = (req, action) => {
    setPendingDecision({ req, action });
    setDecisionNote("");
  };

  const submitDecision = async () => {
    if (!pendingDecision) return;
    const { req, action } = pendingDecision;
    setDecisionBusy(true);
    try {
      await api.post(
        `/coord/assignment-requests/${req.id}/${action}`,
        { note: decisionNote.trim() || null },
        { params: { org_id: orgId } },
      );
      toast.success(action === "approve" ? `${req.teacher_name} assigned to ${req.guest_name}.` : "Denied.");
      setPendingDecision(null);
      setDecisionNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Couldn't ${action}.`);
    } finally {
      setDecisionBusy(false);
    }
  };

  const del = async (t) => {
    if (!window.confirm(`Remove ${t.name}?`)) return;
    await api.delete(`/coord/teachers/${t.id}`, { params: { org_id: orgId } });
    toast.success("Removed.");
    load();
  };

  const teacherCoordIds = (t) => {
    const list = Array.isArray(t.coordinator_ids) ? [...t.coordinator_ids] : [];
    if (t.coordinator_id && !list.includes(t.coordinator_id)) list.push(t.coordinator_id);
    return list;
  };

  const filteredTeachers = useMemo(() => {
    const base = (!canScope || scope === "all") ? teachers : teachers.filter((t) => teacherCoordIds(t).includes(user?.id));
    const lastName = (n) => {
      const parts = (n || "").trim().split(/\s+/);
      return (parts[parts.length - 1] || "").toLowerCase();
    };
    return [...base].sort((a, b) => {
      const cmp = lastName(a.name).localeCompare(lastName(b.name));
      if (cmp !== 0) return cmp;
      return (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
    });
  }, [teachers, scope, user, canScope]);

  const activeCount = filteredTeachers.filter((t) => t.active).length;
  const withLogin = filteredTeachers.filter((t) => t.user_id).length;

  const tl = tq.trim().toLowerCase();
  const shownTeachers = tl ? filteredTeachers.filter((t) => `${t.name} ${t.email || ""} ${t.phone || ""}`.toLowerCase().includes(tl)) : filteredTeachers;

  return (
    <div data-testid="teachers-panel">
      {/* --- Pending assignment requests --- */}
      {requests.length > 0 && (
        <div className="card-surface p-5 mb-6" style={{ background: "var(--bg-alt)", borderColor: "var(--gold)" }} data-testid="assignment-requests-panel">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} style={{ color: "var(--gold)" }} />
            <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Pending assignment requests</h3>
            <span className="ml-1 text-[0.55rem] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full" style={{ background: "var(--gold)", color: "var(--bg-base)", fontWeight: 700 }}>{requests.length}</span>
          </div>
          <p className="text-xs mb-3" style={{ color: "var(--ink-2)" }}>
            Teachers who report to you have asked to be assigned to a guest. Approve to add them to the guest's teacher list.
          </p>
          <div className="space-y-2">
            {requests.map((r) => {
              const active = pendingDecision?.req?.id === r.id;
              return (
              <div key={r.id} className="p-3 rounded-md" style={{ background: "var(--surface-hover)", border: `1px solid ${active ? "var(--ember)" : (r.duplicate_count > 1 ? "var(--gold)" : "var(--border-subtle)")}` }} data-testid={`assignment-request-${r.id}`}>
                <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm" style={{ color: "var(--ink)" }}>
                    <strong>{r.teacher_name}</strong> requested <strong>{r.guest_name}</strong>
                    {r.guest_age_group && <span className="ml-2 text-[0.6rem]" style={{ color: "var(--muted)" }}>{r.guest_age_group}</span>}
                  </div>
                  {r.duplicate_count > 1 && (
                    <div className="text-[0.65rem] mt-1 inline-flex items-center gap-1" style={{ color: "var(--gold)" }} data-testid={`assignment-request-warn-${r.id}`}>
                      ⚠ {r.duplicate_count} teachers have requested this same guest — approve carefully.
                    </div>
                  )}
                  {r.guest_current_teachers > 0 && (
                    <div className="text-[0.65rem] mt-0.5" style={{ color: "var(--muted)" }}>
                      Currently has {r.guest_current_teachers} teacher{r.guest_current_teachers === 1 ? "" : "s"} assigned
                    </div>
                  )}
                </div>
                {!active && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => decide(r, "approve")} className="btn-primary text-xs" data-testid={`assignment-request-approve-${r.id}`}>Approve</button>
                    <button onClick={() => decide(r, "deny")} className="btn-ghost text-xs" data-testid={`assignment-request-deny-${r.id}`}>Deny</button>
                  </div>
                )}
                </div>
                {active && (
                  <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: "var(--border-subtle)" }} data-testid={`assignment-request-note-${r.id}`}>
                    <label className="block text-[0.6rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>
                      Note for {r.teacher_name?.split(" ")[0] || "the teacher"} <span style={{ color: "var(--muted)" }}>(optional)</span>
                    </label>
                    <textarea
                      value={decisionNote}
                      onChange={(e) => setDecisionNote(e.target.value.slice(0, 300))}
                      rows={2}
                      placeholder={pendingDecision.action === "approve"
                        ? "e.g., Great fit — please reach out this week."
                        : "e.g., Already spoken for — let's talk about a different guest."}
                      className="w-full px-3 py-2 text-sm"
                      data-testid={`assignment-request-note-input-${r.id}`}
                      autoFocus
                    />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[0.6rem]" style={{ color: "var(--muted)" }}>{decisionNote.length}/300</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setPendingDecision(null); setDecisionNote(""); }}
                          disabled={decisionBusy}
                          className="btn-ghost text-xs"
                          data-testid={`assignment-request-cancel-${r.id}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={submitDecision}
                          disabled={decisionBusy}
                          className={pendingDecision.action === "approve" ? "btn-primary text-xs" : "btn-ghost text-xs"}
                          style={pendingDecision.action === "deny" ? { color: "var(--terracotta)", border: "1px solid var(--terracotta)" } : undefined}
                          data-testid={`assignment-request-confirm-${r.id}`}
                        >
                          {decisionBusy ? "…" : (pendingDecision.action === "approve" ? "Confirm approve" : "Confirm deny")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-6">
        <StatCard label="Teachers" value={filteredTeachers.length} color="var(--ink)" icon={Users} testid="teacher-stat-total" />
        <StatCard label="Active" value={activeCount} color="var(--olive)" icon={CheckCircle2} testid="teacher-stat-active" />
        <StatCard label="With portal login" value={withLogin} color="var(--ember)" icon={UserCheck} testid="teacher-stat-login" />
      </div>

      <div className="mb-4 space-y-3">
        <TrackerToolbar
          testidPrefix="teachers"
          search={tq}
          onSearch={setTq}
          placeholder="Search teachers…"
          more={canMerge && teachers.length >= 2 ? [{ label: "Merge duplicates…", icon: ArrowLeftRight, onClick: () => setMergeOpen(true), testid: "open-merge-teachers-btn" }] : []}
          primary={<button onClick={() => setModal({ mode: "new" })} className="btn-primary h-10 shrink-0" style={{ padding: "0 1.1rem" }} data-testid="add-teacher-btn"><UserPlus size={15} /> <span className="hidden sm:inline">Add teacher</span></button>}
        />
        {canScope && <TabBar testidPrefix="teacher-scope" value={scope} onChange={setScope} tabs={[{ key: "mine", label: "My teachers" }, { key: "all", label: "All coordinators" }]} />}
      </div>
      {loading ? <p style={{ color: "var(--muted)" }}>Loading…</p> :
        shownTeachers.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title={teachers.length === 0 ? "No teachers yet" : tl ? "No teachers match your search" : "No teachers linked to you yet"}
            description={tl ? "Try a different name, email or phone." : teachers.length === 0
              ? "Add teachers so coordinators can hand off guests once they're ready to begin Home Bible Study."
              : "Switch to “All coordinators” above to see everyone in this church, or add a new teacher — they'll be linked to you by default."}
            action={<button onClick={() => setModal({ mode: "new" })} className="btn-primary" data-testid="empty-add-teacher-btn"><UserPlus size={14} /> Add teacher</button>}
            testid="teachers-empty-state"
          />
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {shownTeachers.map((t) => {
              const manager = t.coordinator_id ? coordinatorsById[t.coordinator_id] : null;
              return (
                <div key={t.id} className="card-surface p-5" data-testid={`teacher-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>{t.name}</h3>
                        {!t.active && <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "var(--muted)", color: "#FFFFFF" }}>Inactive</span>}
                        {t.user_id && <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "var(--olive)", color: "#FFFFFF" }} data-testid={`teacher-has-login-${t.id}`}>Has login</span>}
                      </div>
                      <div className="text-xs space-y-1" style={{ color: "var(--muted)" }}>
                        {t.email && <div className="flex items-center gap-1.5"><Mail size={11} /> {t.email}</div>}
                        {t.phone && <div className="flex items-center gap-1.5"><Phone size={11} /> {t.phone}</div>}
                        {t.city && <div className="flex items-center gap-1.5"><MapPin size={11} /> {t.city}</div>}
                        {t.age_group_pref && <div>Age group: <span style={{ color: "var(--ink-2)" }}>{t.age_group_pref}</span></div>}
                        <div className="flex items-center gap-1.5" data-testid={`teacher-manager-${t.id}`}>
                          <UserCheck size={11} /> Managed by:{" "}
                          <span style={{ color: manager ? "var(--ink-2)" : "var(--muted)" }}>
                            {manager ? (manager.name || manager.email) : "Unassigned"}
                          </span>
                        </div>
                      </div>
                      {(t.interests || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {t.interests.map((i, ix) => <span key={ix} className="text-[0.65rem] px-2 py-0.5 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--olive)" }}>{i}</span>)}
                        </div>
                      )}
                      {t.notes && <p className="mt-3 text-xs italic" style={{ color: "var(--ink-2)" }}>{t.notes}</p>}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button onClick={() => setModal({ mode: "edit", teacher: t })} className="p-1.5 rounded hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid={`teacher-edit-${t.id}`}><Edit3 size={14} /></button>
                      <button onClick={() => del(t)} className="p-1.5 rounded hover:bg-[var(--surface-hover)]" style={{ color: "var(--terracotta)" }} data-testid={`teacher-delete-${t.id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
      {modal?.mode === "new" && (
        <DirectoryPicker
          orgId={orgId}
          title="Add a teacher"
          subtitle="Pick someone from the directory to give them the teacher role. You can add a portal login next."
          actionLabel="Make teacher"
          defaultLifecycle="member"
          testid="teacher-picker"
          onClose={() => setModal(null)}
          onPick={async (p) => {
            await api.post(`/coord/directory/${p.id}/teacher`, {}, { params: { org_id: orgId } });
            const r = await api.get("/coord/teachers", { params: { org_id: orgId } });
            const created = (r.data || []).find((t) => t.guest_id === p.id);
            toast.success(`${formatDisplayName(p)} is now a teacher.`);
            load();
            setModal(created ? { teacher: created } : null);
          }}
        />
      )}
      {modal && modal.mode !== "new" && <TeacherModal teacher={modal.teacher} orgId={orgId} coordinators={coordinators} onClose={() => setModal(null)} onSaved={load} />}
      {mergeOpen && <TeacherMergeModal orgId={orgId} teachers={teachers} onClose={() => setMergeOpen(false)} onDone={load} />}
    </div>
  );
}

// ==================== TEACHER MERGE MODAL ====================
// Admin / org-admin tool: pick two teachers, we rewrite every guest/class reference
// from the "merge" record to the "keep" record and delete the merged record.
function TeacherMergeModal({ orgId, teachers, onClose, onDone }) {
  const [keepId, setKeepId] = useState("");
  const [mergeId, setMergeId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const activeTeachers = useMemo(() => teachers.slice().sort((a, b) => a.name.localeCompare(b.name)), [teachers]);
  const keep = activeTeachers.find((t) => t.id === keepId);
  const merge = activeTeachers.find((t) => t.id === mergeId);

  // Load a merge-preview whenever "merge from" changes.
  useEffect(() => {
    if (!mergeId || !orgId) { setPreview(null); return; }
    setPreviewLoading(true);
    api.get(`/coord/teachers/${mergeId}/merge-preview`, { params: { org_id: orgId } })
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [mergeId, orgId]);

  const canSubmit = keepId && mergeId && keepId !== mergeId && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    if (!window.confirm(`Merge "${merge?.name}" into "${keep?.name}"?\n\nThis rewrites all guest and class assignments and permanently deletes "${merge?.name}". Cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/coord/teachers/merge", { keep_id: keepId, merge_id: mergeId }, { params: { org_id: orgId } });
      const d = r.data || {};
      toast.success(`Merged. Updated ${d.first_steps_guests_updated || 0} First Steps guest${d.first_steps_guests_updated === 1 ? "" : "s"}, ${d.next_steps_guests_updated || 0} Next Steps guest${d.next_steps_guests_updated === 1 ? "" : "s"}, ${d.next_steps_classes_updated || 0} class${d.next_steps_classes_updated === 1 ? "" : "es"}.`);
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't merge.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center py-10 px-4 overflow-y-auto" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose} data-testid="teacher-merge-overlay">
      <div className="w-full max-w-lg rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} onClick={(e) => e.stopPropagation()} data-testid="teacher-merge-modal">
        <div className="p-6 flex items-start justify-between gap-4 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <div className="overline mb-1" style={{ color: "var(--terracotta)", fontSize: "0.6rem" }}>Admin tool</div>
            <h2 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>Merge two teachers</h2>
            <p className="text-xs mt-1 max-w-sm" style={{ color: "var(--muted)" }}>Combine a duplicate teacher record into another so their guest history stays intact.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid="teacher-merge-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Keep this teacher</label>
            <select value={keepId} onChange={(e) => setKeepId(e.target.value)} className="w-full px-3 py-2 text-sm" data-testid="teacher-merge-keep">
              <option value="">Pick the one to keep…</option>
              {activeTeachers.map((t) => <option key={t.id} value={t.id} disabled={t.id === mergeId}>{t.name}{t.email ? ` — ${t.email}` : ""}{t.user_id ? " (has login)" : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Merge this teacher into the kept one</label>
            <select value={mergeId} onChange={(e) => setMergeId(e.target.value)} className="w-full px-3 py-2 text-sm" data-testid="teacher-merge-from">
              <option value="">Pick the duplicate to merge away…</option>
              {activeTeachers.map((t) => <option key={t.id} value={t.id} disabled={t.id === keepId}>{t.name}{t.email ? ` — ${t.email}` : ""}{t.user_id ? " (has login)" : ""}</option>)}
            </select>
          </div>

          {mergeId && (
            <div className="p-3 rounded-md text-xs" style={{ background: "var(--bg-base)", border: "1px solid var(--border-subtle)", color: "var(--ink-2)" }} data-testid="teacher-merge-preview">
              {previewLoading ? (
                <span style={{ color: "var(--muted)" }}>Checking references…</span>
              ) : preview ? (
                <>
                  <div className="font-medium mb-1" style={{ color: "var(--ink)" }}>What will move over:</div>
                  <ul className="space-y-0.5 pl-4 list-disc">
                    <li><strong>{preview.first_steps_guests}</strong> First Steps guest{preview.first_steps_guests === 1 ? "" : "s"}</li>
                    <li><strong>{preview.next_steps_guests}</strong> Next Steps guest teacher assignment{preview.next_steps_guests === 1 ? "" : "s"}</li>
                    <li><strong>{preview.next_steps_classes}</strong> Next Steps class{preview.next_steps_classes === 1 ? "" : "es"}</li>
                  </ul>
                  {merge?.user_id && (
                    <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--border-subtle)" }}>
                      <AlertTriangle size={12} style={{ color: "var(--ember)", display: "inline", marginRight: "0.25rem" }} />
                      The merged teacher&apos;s login will lose teacher access. Their user account (and any other roles) stays.
                    </div>
                  )}
                </>
              ) : (
                <span style={{ color: "var(--muted)" }}>Couldn&apos;t load a preview.</span>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            <button onClick={onClose} className="btn-ghost" data-testid="teacher-merge-cancel">Cancel</button>
            <button onClick={submit} disabled={!canSubmit} className="btn-primary disabled:opacity-40" data-testid="teacher-merge-submit">
              {busy ? "Merging…" : "Merge teachers"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== TEAM ACTIVITY PANEL ====================
const TRACK_COLOR = {
  initial_contact: "var(--ember)",
  monday: "var(--terracotta)",
  home: "var(--olive)",
  first_steps: "var(--gold)",
};
const TRACK_SHORT = {
  initial_contact: "Contact",
  monday: "Intro",
  home: "Home",
  first_steps: "Next Steps",
};

function TeamActivityPanel({ orgId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [mineOnly, setMineOnly] = useState(!user?.is_admin);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    api.get("/coord/team-activity", { params: { org_id: orgId, days, mine_only: mineOnly } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [orgId, days, mineOnly]);

  const activeTeachers = useMemo(
    () => (data?.teachers || []).filter((t) => t.session_count > 0),
    [data]
  );

  if (loading) return <p style={{ color: "var(--muted)" }} data-testid="team-activity-loading">Loading team activity…</p>;
  if (!data) return <p style={{ color: "var(--muted)" }}>Couldn't load activity.</p>;

  const rangeLabel = days === 1 ? "today" : `the last ${days} days`;

  return (
    <div data-testid="team-activity-panel">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
        <StatCard label={`Sessions in ${rangeLabel}`} value={data.totals.sessions} color="var(--ember)" icon={CheckCircle2} testid="team-stat-sessions" />
        <StatCard label="Guests worked with" value={data.totals.guests} color="var(--terracotta)" icon={Users} testid="team-stat-guests" />
        <StatCard label="Active teachers" value={data.totals.teachers} color="var(--olive)" icon={UserCheck} testid="team-stat-active-teachers" />
        <StatCard label="Teachers in scope" value={(data.teachers || []).length} color="var(--ink)" icon={Users} testid="team-stat-scope" hint={mineOnly && !user?.is_admin ? "Yours only" : "All coordinators"} />
      </div>

      {/* Toolbar */}
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>
            {mineOnly && !user?.is_admin ? "My teachers " : "All teachers "}
            <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>this {days === 7 ? "week" : `${days} days`}</span>
          </h2>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            Every session your team marked complete since <strong style={{ color: "var(--ink-2)" }}>{data.since}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!user?.is_admin && (
            <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--surface-hover)" }} data-testid="team-scope-tabs">
              {[[true, "Mine"], [false, "All coordinators"]].map(([k, l]) => (
                <button
                  key={String(k)}
                  onClick={() => setMineOnly(k)}
                  data-testid={`team-scope-${k ? "mine" : "all"}`}
                  className="px-3 py-1 text-xs rounded-full transition-colors"
                  style={{ background: mineOnly === k ? "var(--ink)" : "transparent", color: mineOnly === k ? "#FFFFFF" : "var(--ink-2)", fontWeight: mineOnly === k ? 600 : 400 }}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
          <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--surface-hover)" }} data-testid="team-days-tabs">
            {[[7, "7 days"], [30, "30 days"], [90, "90 days"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setDays(k)}
                data-testid={`team-days-${k}`}
                className="px-3 py-1 text-xs rounded-full transition-colors"
                style={{ background: days === k ? "var(--ember)" : "transparent", color: days === k ? "#FFFFFF" : "var(--ink-2)", fontWeight: days === k ? 600 : 400 }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {(data.teachers || []).length === 0 ? (
        <EmptyState
          icon={Users}
          title={mineOnly && !user?.is_admin ? "No teachers linked to you yet" : "No teachers in this church yet"}
          description={mineOnly && !user?.is_admin
            ? "Switch to “All coordinators” above to see everyone in the org, or head to the Teachers tab to add your first teacher."
            : "Head to the Teachers tab to add your first teacher — their weekly activity will start showing up here."}
          testid="team-activity-empty"
        />
      ) : activeTeachers.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title={`No sessions completed in ${rangeLabel}`}
          description={`When your teachers check off a session in the guest tracker, it'll roll up here — perfect for a Monday-morning "what did my team accomplish?" review.`}
          testid="team-activity-quiet"
        />
      ) : (
        <div className="space-y-5">
          {activeTeachers.map((t) => (
            <div key={t.teacher.id} className="card-surface p-5" data-testid={`team-teacher-card-${t.teacher.id}`}>
              <header className="flex items-center justify-between gap-3 flex-wrap mb-4 pb-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
                <div>
                  <div className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Teacher</div>
                  <h3 className="font-serif-display text-xl lg:text-2xl" style={{ color: "var(--ink)" }}>{t.teacher.name}</h3>
                </div>
                <div className="flex items-center gap-4 text-xs" style={{ color: "var(--muted)" }}>
                  <div className="text-right">
                    <div className="text-[0.6rem] uppercase tracking-[0.18em]">Sessions</div>
                    <div className="font-serif-display text-2xl" style={{ color: "var(--ember)" }} data-testid={`team-teacher-sessions-${t.teacher.id}`}>{t.session_count}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[0.6rem] uppercase tracking-[0.18em]">Guests</div>
                    <div className="font-serif-display text-2xl" style={{ color: "var(--terracotta)" }}>{t.guest_count}</div>
                  </div>
                </div>
              </header>

              <div className="space-y-4">
                {t.guests.map((gr) => (
                  <div key={gr.guest.id} data-testid={`team-guest-block-${t.teacher.id}-${gr.guest.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Users size={12} style={{ color: "var(--muted)" }} />
                        <span className="text-sm font-medium truncate" style={{ color: "var(--ink)" }}>
                          {formatDisplayName(gr.guest)}
                        </span>
                        <span className="text-[0.65rem] px-2 py-0.5 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }}>
                          {gr.sessions.length} session{gr.sessions.length === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                    <ul className="space-y-1 pl-5">
                      {gr.sessions.map((s, i) => (
                        <li key={i} className="flex items-start gap-3 text-xs" data-testid={`team-session-${t.teacher.id}-${gr.guest.id}-${i}`}>
                          <span
                            className="mt-1 shrink-0 rounded-full"
                            style={{ width: 6, height: 6, background: TRACK_COLOR[s.track] || "var(--muted)" }}
                          />
                          <div className="flex-1 min-w-0">
                            <span style={{ color: "var(--ink)" }}>{s.title}</span>
                            <span className="ml-2" style={{ color: "var(--muted)" }}>· {TRACK_SHORT[s.track] || s.track}</span>
                            {s.notes && (
                              <div className="mt-0.5 text-[0.7rem] italic pl-3 border-l-2" style={{ color: "var(--ink-2)", borderColor: "var(--border-subtle)" }}>
                                “{s.notes.length > 140 ? s.notes.slice(0, 140) + "…" : s.notes}”
                              </div>
                            )}
                          </div>
                          <span className="shrink-0 text-[0.65rem] tabular-nums" style={{ color: "var(--muted)" }}>
                            {new Date(s.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateClassModal({ orgId, teachers, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [teacherIds, setTeacherIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the class a name."); return; }
    setBusy(true);
    try {
      const r = await api.post("/coord/next-steps-classes", { name: name.trim(), teacher_ids: teacherIds }, { params: { org_id: orgId } });
      toast.success("Class created.");
      onCreated(r.data);
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't create class."); }
    finally { setBusy(false); }
  };
  const toggle = (id) => {
    const s = new Set(teacherIds);
    if (s.has(id)) s.delete(id);
    else if (s.size < 2) s.add(id);
    else { toast.error("Only 2 teachers allowed."); return; }
    setTeacherIds([...s]);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-lg card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="ns-create-class-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-6" style={{ color: "var(--ink)" }}>Create a Next Steps class</h2>
        <div className="space-y-5">
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Class name *</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Spring 2026 · Sunday nights" className="w-full px-3 py-2 text-sm" data-testid="ns-create-name" />
          </div>
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--muted)" }}>Teachers (up to 2)</label>
            {teachers.length === 0 ? (
              <p className="text-xs italic" style={{ color: "var(--muted)" }}>No teachers yet. Add teachers in the Teachers tab, or leave blank and assign later.</p>
            ) : (
              <div className="space-y-1">
                {teachers.filter((t) => t.active).map((t) => (
                  <button key={t.id} type="button" onClick={() => toggle(t.id)} className="w-full text-left p-2 rounded-md border flex items-center gap-2" style={{ borderColor: teacherIds.includes(t.id) ? "var(--gold)" : "var(--border-subtle)", background: teacherIds.includes(t.id) ? "var(--surface-hover)" : "transparent" }} data-testid={`ns-create-teacher-${t.id}`}>
                    {teacherIds.includes(t.id) ? <CheckCircle2 size={16} style={{ color: "var(--gold)" }} /> : <Circle size={16} style={{ color: "var(--muted)" }} />}
                    <span className="text-sm" style={{ color: "var(--ink)" }}>{t.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="ns-create-submit">{busy ? "Creating…" : "Create class"}</button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function EditDiscipleModal({ orgId, classId, disciple, onClose, onSaved }) {
  // If legacy record has only `name`, split at last space so the form is still usable.
  const initialFirst = disciple.first_name || (disciple.name || "").trim().split(/\s+/).slice(0, -1).join(" ");
  const initialLast = disciple.last_name || (disciple.name || "").trim().split(/\s+/).slice(-1)[0] || "";
  const [form, setForm] = useState({
    first_name: initialFirst || "",
    last_name: initialLast || "",
    gender: disciple.gender || null,
    phone: disciple.phone || "",
    email: disciple.email || "",
    notes: disciple.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!(form.first_name.trim() || form.last_name.trim())) {
      toast.error("First or last name is required."); return;
    }
    setBusy(true);
    try {
      await api.put(`/coord/next-steps-classes/${classId}/disciples/${disciple.id}`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        gender: form.gender,
        phone: form.phone.trim(),
        email: form.email.trim(),
        notes: form.notes,
      }, { params: { org_id: orgId } });
      toast.success("Saved.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-lg card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="ns-edit-disciple-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-4" style={{ color: "var(--ink)" }}>Edit disciple</h2>
        {disciple.guest_id && (
          <p className="text-[0.65rem] italic mb-3" style={{ color: "var(--muted)" }}>Originally added from the guest list. Edits here only affect this class roster; the guest record isn't changed.</p>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>First name *</label>
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="ns-edit-first_name" />
            </div>
            <div>
              <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Last name *</label>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="ns-edit-last_name" />
            </div>
          </div>
          {[["phone", "Phone"], ["email", "Email", "email"]].map(([k, label, type]) => (
            <div key={k}>
              <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>{label}</label>
              <input type={type || "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid={`ns-edit-${k}`} />
            </div>
          ))}
          <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="ns-edit-gender" />
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Notes</label>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="ns-edit-notes" />
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="ns-edit-submit">{busy ? "Saving…" : "Save changes"}</button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function MoveDiscipleModal({ orgId, sourceClass, disciple, allClasses, onClose, onMoved }) {
  const [targetClassId, setTargetClassId] = useState("");
  const [resetAttendance, setResetAttendance] = useState(false);
  const [busy, setBusy] = useState(false);
  const candidates = allClasses.filter((c) => c.id !== sourceClass.id && c.active !== false);

  const submit = async (e) => {
    e.preventDefault();
    if (!targetClassId) { toast.error("Pick a destination class."); return; }
    setBusy(true);
    try {
      await api.post(
        `/coord/next-steps-classes/${sourceClass.id}/disciples/${disciple.id}/move`,
        { target_class_id: targetClassId, reset_attendance: resetAttendance },
        { params: { org_id: orgId } },
      );
      const target = candidates.find((c) => c.id === targetClassId);
      toast.success(`Moved ${discipleDisplayName(disciple)} to ${target?.name || "the new class"}.`);
      onMoved(targetClassId);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="ns-move-disciple-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-1" style={{ color: "var(--ink)" }}>Move to another class</h2>
        <p className="text-xs mb-5" style={{ color: "var(--muted)" }}>
          Moving <strong style={{ color: "var(--ink-2)" }}>{discipleDisplayName(disciple)}</strong> from <strong style={{ color: "var(--ink-2)" }}>{sourceClass.name}</strong>.
        </p>

        {candidates.length === 0 ? (
          <div className="p-4 rounded-md text-sm" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }}>
            You don't have any other Next Steps classes yet. Create another class first, then come back to move this disciple.
          </div>
        ) : (
          <>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Destination class *</label>
            <select value={targetClassId} onChange={(e) => setTargetClassId(e.target.value)} className="w-full px-3 py-2 text-sm mb-4" data-testid="ns-move-target-select">
              <option value="">— Pick a class —</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <label className="flex items-start gap-2 p-3 rounded-md cursor-pointer hover:bg-[var(--surface-hover)] mb-4" style={{ border: "1px solid var(--border-subtle)" }}>
              <input type="checkbox" checked={resetAttendance} onChange={(e) => setResetAttendance(e.target.checked)} data-testid="ns-move-reset-checkbox" className="mt-1" />
              <div>
                <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>Reset attendance in the new class</div>
                <div className="text-[0.7rem] mt-0.5" style={{ color: "var(--muted)" }}>
                  {resetAttendance
                    ? "Their attendance will start blank — all 24 lessons unchecked."
                    : "Their attendance from this class will carry over 1:1."}
                </div>
              </div>
            </label>
          </>
        )}

        <div className="flex gap-2">
          <button type="submit" disabled={busy || candidates.length === 0 || !targetClassId} className="btn-primary" data-testid="ns-move-submit">
            {busy ? "Moving…" : "Move disciple"}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}


function ClassRosterView({ cls, orgId, teachers, guests, allClasses, onBack, onReload, onNavigateToClass, onSetActive }) {
  const [lessonIndex, setLessonIndex] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // disciple being edited (fallback for legacy no-guest_id)
  const [profileGuestId, setProfileGuestId] = useState(null); // when set, opens the full GuestProfile
  const [moving, setMoving] = useState(null); // disciple being moved
  const [busyTid, setBusyTid] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"
  const [sortKey, setSortKey] = useState("name"); // "name" | "attended" | "streak"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  const [downloading, setDownloading] = useState(false);
  const [resources, setResources] = useState({});
  const [churchName, setChurchName] = useState("");

  useEffect(() => {
    api.get("/coord/text-examples", { params: { org_id: orgId } }).then((r) => setChurchName(r.data.church_name || "")).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    api.get("/coord/lesson-resources", { params: { org_id: orgId } })
      .then((r) => setResources(r.data.resources || {}))
      .catch(() => setResources({}));
  }, [orgId]);

  const profileGuest = useMemo(() => guests.find((g) => g.id === profileGuestId) || null, [guests, profileGuestId]);

  // Click on a disciple — opens their full contact card from the directory.
  // Fallback: if the disciple has no guest_id link, use the compact EditDiscipleModal.
  const openDiscipleProfile = (d) => {
    if (d?.guest_id) setProfileGuestId(d.guest_id);
    else setEditing(d);
  };

  // ---- Absent-streak calculation ----
  // The class has "covered" every lesson up to the highest index where ANY
  // disciple has been marked present. For each disciple, count trailing
  // consecutive absents (completed=false) from that covered index backwards
  // until the first present. Streak >= 2 triggers the alert.
  const { streaks, maxCovered } = useMemo(() => {
    let maxIdx = -1;
    (cls.disciples || []).forEach((d) => {
      (d.attendance || []).forEach((a, i) => {
        if (a?.completed && i > maxIdx) maxIdx = i;
      });
    });
    const map = {};
    (cls.disciples || []).forEach((d) => {
      if (maxIdx < 0) { map[d.id] = 0; return; }
      const att = d.attendance || [];
      let s = 0;
      for (let i = maxIdx; i >= 0; i--) {
        if (att[i]?.completed) break;
        s++;
      }
      map[d.id] = s;
    });
    return { streaks: map, maxCovered: maxIdx };
  }, [cls.disciples]);

  const sortedDisciples = useMemo(() => {
    const arr = [...(cls.disciples || [])];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let av, bv;
      if (sortKey === "attended") {
        av = (a.attendance || []).filter((x) => x?.completed).length;
        bv = (b.attendance || []).filter((x) => x?.completed).length;
      } else if (sortKey === "streak") {
        av = streaks[a.id] || 0;
        bv = streaks[b.id] || 0;
      } else {
        av = discipleSortKey(a);
        bv = discipleSortKey(b);
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [cls.disciples, sortKey, sortDir, streaks]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "name" ? "asc" : "desc"); }
  };

  const markAttendance = async (disciple, present, dateOverride, idxOverride) => {
    const idx = idxOverride ?? lessonIndex;
    setBusyTid(`${disciple.id}-${idx}`);
    try {
      const date = dateOverride || (present ? new Date().toISOString().slice(0, 10) : null);
      await api.put(`/coord/next-steps-classes/${cls.id}/attendance`, {
        disciple_id: disciple.id, lesson_index: idx, present, date,
      }, { params: { org_id: orgId } });
      onReload();
    } catch { toast.error("Couldn't save."); }
    finally { setBusyTid(null); }
  };

  const updateNote = async (disciple, notes) => {
    try {
      await api.put(`/coord/next-steps-classes/${cls.id}/attendance`, {
        disciple_id: disciple.id, lesson_index: lessonIndex, notes,
      }, { params: { org_id: orgId } });
      onReload();
    } catch { toast.error("Couldn't save note."); }
  };

  const removeDisciple = async (d) => {
    if (!window.confirm(`Remove ${discipleDisplayName(d)} from the class?`)) return;
    try {
      await api.delete(`/coord/next-steps-classes/${cls.id}/disciples/${d.id}`, { params: { org_id: orgId } });
      toast.success("Removed.");
      onReload();
    } catch { toast.error("Couldn't remove."); }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/coord/next-steps-classes/${cls.id}/export`, {
        params: { org_id: orgId },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const safe = (cls.name || "class").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 80);
      link.setAttribute("download", `${safe}_roster.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Roster downloaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't export.");
    } finally {
      setDownloading(false);
    }
  };

  const present = cls.disciples.reduce((s, d) => s + (d.attendance?.[lessonIndex]?.completed ? 1 : 0), 0);
  const flaggedCount = Object.values(streaks).filter((n) => n >= 2).length;

  return (
    <div data-testid={`ns-class-view-${cls.id}`}>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={onBack} className="text-xs mb-2 hover:underline flex items-center gap-1" style={{ color: "var(--ink-2)" }} data-testid="ns-back-to-list"><ArrowRight size={12} style={{ transform: "rotate(180deg)" }} /> All classes</button>
          <h2 className="font-serif-display text-3xl leading-tight" style={{ color: "var(--ink)" }}>{cls.name}</h2>
          {cls.active === false && (
            <span className="inline-flex mt-1 text-[0.6rem] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full" style={{ border: "1px solid var(--olive)", color: "var(--olive)" }} data-testid="ns-class-finished-badge">
              Finished{cls.finished_at ? ` · ${new Date(cls.finished_at).toLocaleDateString()}` : ""}
            </span>
          )}
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {cls.disciples.length} disciple{cls.disciples.length === 1 ? "" : "s"}
            {cls.teachers.length > 0 && <> · Taught by <span style={{ color: "var(--ink-2)" }}>{cls.teachers.map((t) => t.name).join(" & ")}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--surface-hover)" }} data-testid="ns-view-toggle">
            {[["list", List, "Lesson"], ["grid", LayoutGrid, "Grid"]].map(([k, Icon, label]) => (
              <button key={k} onClick={() => setViewMode(k)} data-testid={`ns-view-${k}`} className="px-3 py-1 text-xs rounded-full transition-colors flex items-center gap-1.5" style={{ background: viewMode === k ? "var(--ink)" : "transparent", color: viewMode === k ? "var(--bg-base)" : "var(--ink-2)", fontWeight: viewMode === k ? 600 : 400 }}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
          <button onClick={downloadCsv} disabled={downloading || cls.disciples.length === 0} className="btn-ghost" data-testid="ns-download-csv">
            <Download size={14} /> {downloading ? "Preparing…" : "Download CSV"}
          </button>
          {cls.active === false ? (
            <button onClick={() => onSetActive(true)} className="btn-primary" data-testid="ns-reopen-class-btn"><RotateCcw size={14} /> Reopen class</button>
          ) : (
            <>
              <button onClick={() => onSetActive(false)} className="btn-ghost" data-testid="ns-finish-class-btn"><CheckCircle2 size={14} /> Finish class</button>
              <button onClick={() => setAddOpen(true)} className="btn-primary" data-testid="ns-add-disciple-btn"><UserPlus size={14} /> Add disciple</button>
            </>
          )}
        </div>
      </div>

      {flaggedCount > 0 && (
        <div className="mb-5 p-3 rounded-md border-l-4 flex items-start gap-3 flex-wrap" style={{ background: "var(--surface-hover)", borderColor: "var(--terracotta)" }} data-testid="ns-streak-banner">
          <AlertTriangle size={18} style={{ color: "var(--terracotta)" }} className="mt-0.5 shrink-0" />
          <div className="text-sm" style={{ color: "var(--ink-2)" }}>
            <strong style={{ color: "var(--terracotta)" }}>{flaggedCount}</strong> disciple{flaggedCount === 1 ? " is" : "s are"} missing 2+ lessons in a row. Consider reaching out this week.
          </div>
        </div>
      )}

      {viewMode === "list" ? (
        <>
          {/* Lesson picker */}
          <div className="mb-6 overflow-x-auto -mx-2 px-2">
            <div className="flex gap-1.5 flex-wrap" data-testid="ns-lesson-tabs">
              {NS_LESSON_TITLES.map((title, i) => (
                <button key={i} onClick={() => setLessonIndex(i)} data-testid={`ns-lesson-${i}`} className="text-xs px-2.5 py-1 rounded-full transition-colors" style={{ background: lessonIndex === i ? "var(--gold)" : "var(--surface-hover)", color: lessonIndex === i ? "#FFFFFF" : "var(--ink-2)", fontWeight: lessonIndex === i ? 600 : 400 }} title={title}>
                  L{i + 1}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4 p-4 rounded-md" style={{ background: "var(--surface-hover)" }}>
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="md:w-64 shrink-0 min-w-0">
                <div className="text-[0.6rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>Lesson {lessonIndex + 1} of 24</div>
                <h3 className="font-serif-display text-xl min-w-0"><LessonTitleLink index={lessonIndex} resources={resources} testid="ns-lesson-title-link" /></h3>
                <div className="text-sm mt-1" style={{ color: "var(--ink-2)" }} data-testid="ns-lesson-present-count">
                  <strong style={{ color: "var(--gold)" }}>{present}</strong> of {cls.disciples.length} present
                </div>
              </div>
              <LessonLinkBoxes index={lessonIndex} resources={resources} />
            </div>
          </div>

          {cls.disciples.length === 0 ? (
            <EmptyState icon={Users} title="No disciples yet" description={`Add disciples from your guest list or manually. Once added, mark their attendance for each lesson.`} action={<button onClick={() => setAddOpen(true)} className="btn-primary"><UserPlus size={14} /> Add the first disciple</button>} testid="ns-class-empty" />
          ) : (
            <div className="space-y-2" data-testid="ns-attendance-list">
              {sortedDisciples.map((d) => (
                <NsDiscipleRow key={d.id} d={d} orgId={orgId} churchName={churchName} lessonIndex={lessonIndex} streak={streaks[d.id] || 0} busyTid={busyTid} resources={resources}
                  onMark={markAttendance} onNote={updateNote} onOpen={openDiscipleProfile} onMove={setMoving} onRemove={removeDisciple} />
              ))}
            </div>
          )}
        </>
      ) : (
        <AttendanceGrid
          disciples={sortedDisciples}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={toggleSort}
          streaks={streaks}
          maxCovered={maxCovered}
          busyTid={busyTid}
          onToggle={(d, i) => markAttendance(d, !d.attendance?.[i]?.completed, undefined, i)}
          onEdit={(d) => openDiscipleProfile(d)}
        />
      )}

      {addOpen && (
        <DirectoryPicker
          orgId={orgId}
          title={`Add to ${cls.name}`}
          subtitle="Pick anyone in the directory — they join this class without creating a new profile."
          actionLabel="Add to class"
          defaultLifecycle="disciple"
          testid="ns-picker"
          onClose={() => setAddOpen(false)}
          onPick={async (p) => {
            await api.post(`/coord/next-steps-classes/${cls.id}/disciples`, { guest_id: p.id }, { params: { org_id: orgId } });
            toast.success(`${formatDisplayName(p)} added to class.`);
            onReload?.();
          }}
        />
      )}
      {editing && <EditDiscipleModal orgId={orgId} classId={cls.id} disciple={editing} onClose={() => setEditing(null)} onSaved={onReload} />}
      {profileGuest && (
        <GuestProfile
          guest={profileGuest}
          teachers={teachers}
          orgId={orgId}
          onClose={() => setProfileGuestId(null)}
          onSaved={onReload}
        />
      )}
      {moving && <MoveDiscipleModal orgId={orgId} sourceClass={cls} disciple={moving} allClasses={allClasses || []} onClose={() => setMoving(null)} onMoved={(targetId) => { onReload(); onNavigateToClass?.(targetId); }} />}
    </div>
  );
}


function NextStepsPanel({ orgId }) {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = (opts = {}) => {
    if (!orgId) return;
    if (!opts.silent) setLoading(true);
    const params = { org_id: orgId };
    Promise.all([
      api.get("/coord/next-steps-classes", { params }),
      api.get("/coord/teachers", { params }),
      api.get("/coord/guests", { params }),
      api.get("/coord/inquiries", { params }).catch(() => ({ data: { inquiries: [] } })),
    ]).then(([c, t, g, inq]) => {
      setClasses(c.data || []);
      setTeachers(t.data || []);
      setGuests(g.data || []);
      setInquiries(inq.data?.inquiries || []);
    }).finally(() => { if (!opts.silent) setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orgId]);
  const reloadSilent = () => load({ silent: true });

  const selected = classes.find((c) => c.id === selectedId);

  // Guests who were graduated (phase2_enrolled) but haven't been added to any Next Steps class yet.
  // These are the people the coordinator still needs to place. Match by guest_id.
  const pendingGuests = useMemo(() => {
    const enrolled = new Set();
    classes.forEach((c) => (c.disciples || []).forEach((d) => d.guest_id && enrolled.add(d.guest_id)));
    return guests
      .filter((g) => g.phase2_enrolled && !enrolled.has(g.id) && !g.archived)
      .sort((a, b) => (b.phase2_enrolled_at || "").localeCompare(a.phase2_enrolled_at || ""));
  }, [guests, classes]);

  const [pendingBusy, setPendingBusy] = useState(null); // guest id currently being assigned
  const activeClasses = classes.filter((c) => c.active !== false);
  const finishedClasses = classes.filter((c) => c.active === false);
  const [showFinished, setShowFinished] = useState(false);
  const setClassActive = async (c, active) => {
    if (!active && !window.confirm(`Finish "${c.name}"? It moves to Finished classes. You can reopen it anytime.`)) return;
    try {
      await api.put(`/coord/next-steps-classes/${c.id}`, { active }, { params: { org_id: orgId } });
      toast.success(active ? `${c.name} reopened.` : `${c.name} finished.`);
      reloadSilent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the class."); }
  };
  const returnToDirectory = async (g) => {
    if (!window.confirm(`Return ${formatDisplayName(g)} to the directory? They'll leave this list but stay in your directory.`)) return;
    setPendingBusy(g.id);
    try {
      await api.post(`/coord/guests/${g.id}/return-to-directory`, {}, { params: { org_id: orgId } });
      toast.success(`${g.first_name || "They"} returned to the directory.`);
      reloadSilent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
    finally { setPendingBusy(null); }
  };
  const assignPendingToClass = async (guest, classId) => {
    if (!classId) return;
    setPendingBusy(guest.id);
    try {
      const r = await api.post(`/coord/next-steps-classes/${classId}/disciples`, { guest_id: guest.id }, { params: { org_id: orgId } });
      toast.success(`${guest.first_name} added to the class.`);
      // Refresh classes so the guest disappears from the pending list.
      reloadSilent();
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to class.");
    } finally {
      setPendingBusy(null);
    }
  };

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading…</p>;

  if (selected) {
    return <ClassRosterView cls={selected} orgId={orgId} teachers={teachers} guests={guests} allClasses={classes} onBack={() => setSelectedId(null)} onReload={reloadSilent} onSetActive={(a) => setClassActive(selected, a)} />;
  }

  return (
    <div data-testid="next-steps-panel">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>Next Steps classes</h2>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>Each class walks a group of disciples through the 24-lesson series. Add disciples from your guest list or manually.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="ns-create-class-btn"><Plus size={14} /> New class</button>
      </div>

      <InquiriesSection orgId={orgId} inquiries={inquiries} onChanged={reloadSilent} />

      {/* Pending class-assignment queue — graduated First Steps guests not yet placed. */}
      {pendingGuests.length > 0 && (
        <section className="mb-8 card-surface p-5" style={{ borderLeft: "3px solid var(--gold)" }} data-testid="ns-pending-assignment">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="overline flex items-center gap-2" style={{ fontSize: "0.6rem" }}>
                <GraduationCap size={12} style={{ color: "var(--gold)" }} /> Graduated · needs a class ({pendingGuests.length})
              </div>
              <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--muted)" }}>
                These guests finished First Steps and are ready for a Next Steps class. Pick a class, or return them to the directory if they won't be joining one.
              </p>
            </div>
          </div>

          <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
            {pendingGuests.map((g) => (
              <li key={g.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`ns-pending-row-${g.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{formatDisplayName(g)}</div>
                  <div className="text-[0.65rem] uppercase tracking-[0.14em] mt-0.5" style={{ color: "var(--muted)" }}>
                    Graduated{g.phase2_enrolled_at ? ` · ${new Date(g.phase2_enrolled_at).toLocaleDateString()}` : ""}
                    {g.email ? <> · {g.email}</> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => returnToDirectory(g)} disabled={pendingBusy === g.id} className="text-xs px-3 py-1.5 rounded-full transition-colors hover:bg-[var(--surface-hover)]" style={{ border: "1px solid var(--border-strong)", color: "var(--ink-2)" }} data-testid={`ns-pending-return-${g.id}`}>
                  Return to directory
                </button>
                {activeClasses.length === 0 ? (
                  <span className="text-xs" style={{ color: "var(--muted)" }}>Create a class first ↑</span>
                ) : (
                  <select
                    defaultValue=""
                    onChange={(e) => assignPendingToClass(g, e.target.value)}
                    disabled={pendingBusy === g.id}
                    className="px-2 py-1 text-sm border"
                    style={{ borderColor: "var(--border-strong)", background: "var(--bg-alt)" }}
                    data-testid={`ns-pending-assign-${g.id}`}
                  >
                    <option value="" disabled>Add to class…</option>
                    {activeClasses.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {activeClasses.length === 0 ? (
        <EmptyState icon={GraduationCap} title={finishedClasses.length ? "No active classes" : "No Next Steps classes yet"} description="Create a class to start tracking attendance across the 24-lesson series." action={<button onClick={() => setCreating(true)} className="btn-primary" data-testid="ns-empty-create-btn"><Plus size={14} /> Create a class</button>} testid="ns-classes-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activeClasses.map((c) => <NsClassCard key={c.id} c={c} onOpen={() => setSelectedId(c.id)} />)}
        </div>
      )}

      {finishedClasses.length > 0 && (
        <section className="mt-8" data-testid="ns-finished-classes">
          <button onClick={() => setShowFinished((v) => !v)} className="overline flex items-center gap-2 mb-3" style={{ fontSize: "0.6rem", color: "var(--muted)" }} data-testid="ns-finished-toggle">
            <ChevronRight size={12} style={{ transform: showFinished ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} /> Finished classes ({finishedClasses.length})
          </button>
          {showFinished && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {finishedClasses.map((c) => <NsClassCard key={c.id} c={c} onOpen={() => setSelectedId(c.id)} onReopen={() => setClassActive(c, true)} />)}
            </div>
          )}
        </section>
      )}

      {creating && <CreateClassModal orgId={orgId} teachers={teachers} onClose={() => setCreating(false)} onCreated={(c) => { setClasses([c, ...classes]); setSelectedId(c.id); }} />}

      <LessonResourcesLibrary orgId={orgId} />
    </div>
  );
}

// -----------------------------------------------------------------
// Inquiries — public QR intake landing on Next Steps page.
// -----------------------------------------------------------------
function InquiriesSection({ orgId, inquiries, onChanged }) {
  const [qr, setQr] = useState(null); // { code, org_name }
  const [showQr, setShowQr] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [flyerBusy, setFlyerBusy] = useState(false);
  const [syllabusBusy, setSyllabusBusy] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    api.get("/coord/inquiry-qr-code", { params: { org_id: orgId } })
      .then((r) => setQr(r.data))
      .catch(() => setQr(null));
  }, [orgId]);

  const publicUrl = qr?.code ? `${window.location.origin}/inquiry/${qr.code}` : "";

  const copyLink = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied."); }
    catch { toast.error("Couldn't copy — select & copy manually."); }
  };

  const downloadFlyer = async () => {
    if (!orgId) return;
    setFlyerBusy(true);
    try {
      const res = await api.get("/coord/inquiry-flyer.pdf", {
        params: { org_id: orgId },
        responseType: "blob",
      });
      // Use the Content-Disposition filename when the browser surfaces it,
      // otherwise fall back to a safe default.
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `FLYER_QR_INQUIRY_${(qr?.org_name || "Church").replace(/\s+/g, "_")}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Flyer downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't download the flyer.");
    } finally {
      setFlyerBusy(false);
    }
  };

  const downloadSyllabus = async () => {
    setSyllabusBusy(true);
    try {
      const res = await api.get("/coord/course-syllabus.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "FLYER_COURSE_SYLLABUS.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Syllabus downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't download the syllabus.");
    } finally {
      setSyllabusBusy(false);
    }
  };

  const convert = async (inq) => {
    setBusyId(inq.id);
    try {
      await api.post(`/coord/inquiries/${inq.id}/convert`, {}, { params: { org_id: orgId } });
      toast.success(`${inq.first_name} added to the pending-class queue.`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't convert inquiry.");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (inq) => {
    if (!window.confirm(`Dismiss the inquiry from ${inq.first_name} ${inq.last_name}?`)) return;
    setBusyId(inq.id);
    try {
      await api.delete(`/coord/inquiries/${inq.id}`, { params: { org_id: orgId } });
      toast.success("Inquiry dismissed.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't dismiss inquiry.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="mb-8 card-surface p-5" style={{ borderLeft: "3px solid var(--terracotta)" }} data-testid="ns-inquiries">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2" style={{ fontSize: "0.6rem" }}>
            <UserPlus size={12} style={{ color: "var(--terracotta)" }} /> Inquiries ({inquiries.length})
          </div>
          <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--muted)" }}>
            Public QR landing where visitors ask about joining a Next Steps class. Convert turns the inquiry into a disciple ready for class placement above.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowQr(true)} disabled={!qr?.code} className="btn-ghost" data-testid="ns-inquiry-qr-btn">
            <QrCode size={14} /> Show QR
          </button>
          <button onClick={copyLink} disabled={!publicUrl} className="btn-ghost" data-testid="ns-inquiry-copy-link">
            <LinkIcon size={14} /> Copy link
          </button>
          <button onClick={downloadFlyer} disabled={flyerBusy || !orgId} className="btn-ghost" data-testid="ns-inquiry-flyer-btn">
            <Printer size={14} /> {flyerBusy ? "Preparing…" : "Download flyer (PDF)"}
          </button>
          <button onClick={downloadSyllabus} disabled={syllabusBusy} className="btn-ghost" data-testid="ns-inquiry-syllabus-btn">
            <FileText size={14} /> {syllabusBusy ? "Preparing…" : "Download syllabus (PDF)"}
          </button>
        </div>
      </div>

      {inquiries.length === 0 ? (
        <p className="text-xs italic py-2" style={{ color: "var(--muted)" }}>
          No open inquiries right now. Share the QR after service — new submissions will land here.
        </p>
      ) : (
        <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
          {inquiries.map((inq) => (
            <li key={inq.id} className="py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`ns-inquiry-row-${inq.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{formatDisplayName(inq)}</div>
                <div className="text-[0.65rem] uppercase tracking-[0.14em] mt-0.5" style={{ color: "var(--muted)" }}>
                  Received {new Date(inq.received_at).toLocaleDateString()}
                  {inq.email ? <> · {inq.email}</> : null}
                  {inq.phone ? <> · {inq.phone}</> : null}
                </div>
                {(inq.address_city || inq.age_group || inq.referral_source || inq.invited_by) && (
                  <div className="text-[0.65rem] mt-1" style={{ color: "var(--ink-2)" }}>
                    {[inq.address_city, inq.age_group, inq.referral_source && `via ${inq.referral_source}`, inq.invited_by && `invited by ${inq.invited_by}`]
                      .filter(Boolean).join(" · ")}
                  </div>
                )}
                {inq.notes && (
                  <div className="text-xs mt-1 italic" style={{ color: "var(--ink-2)" }}>"{inq.notes}"</div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => convert(inq)}
                  disabled={busyId === inq.id}
                  className="btn-primary text-xs"
                  style={{ padding: "0.4rem 0.7rem" }}
                  data-testid={`ns-inquiry-convert-${inq.id}`}
                >
                  {busyId === inq.id ? "Working…" : "Convert"}
                </button>
                <button
                  onClick={() => dismiss(inq)}
                  disabled={busyId === inq.id}
                  className="btn-ghost text-xs"
                  style={{ padding: "0.4rem 0.7rem" }}
                  data-testid={`ns-inquiry-dismiss-${inq.id}`}
                >
                  Dismiss
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showQr && qr?.code && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={() => setShowQr(false)}
          data-testid="ns-inquiry-qr-modal"
        >
          <div
            className="bg-white rounded-lg p-8 max-w-sm text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif-display text-2xl mb-1" style={{ color: "#0A1B33" }}>Scan to inquire</h3>
            <p className="text-xs mb-4" style={{ color: "#64748b" }}>{qr.org_name} · Next Steps class</p>
            <div className="p-4 inline-block" style={{ background: "#FFFFFF" }}>
              <QRCodeSVG value={publicUrl} size={240} level="M" includeMargin={false} />
            </div>
            <div className="text-xs mt-4 break-all" style={{ color: "#64748b" }}>{publicUrl}</div>
            <button onClick={() => setShowQr(false)} className="btn-ghost mt-4" style={{ color: "#0A1B33" }}>Close</button>
          </div>
        </div>
      )}
    </section>
  );
}

// ==================== MAIN PAGE ====================
// -------- Team roster (Org Admins see & manage coordinator toggles) --------
function TeamRosterPanel({ orgId, isSysAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const url = isSysAdmin ? `/admin/orgs/${orgId}/members` : `/coord/my-org/members`;
      const r = await api.get(url);
      setData(r.data);
    } catch {
      toast.error("Couldn't load the roster.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (orgId) load(); /* eslint-disable-next-line */ }, [orgId]);

  const toggleCoord = async (userId, on) => {
    try {
      await api.put(`/admin/users/${userId}/toggle-coordinator`, { on });
      toast.success(on ? "Coordinator access granted." : "Coordinator access revoked.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update role.");
    }
  };

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading roster…</p>;
  if (!data) return null;

  const roleRank = (m) => (m.is_org_admin ? 0 : m.is_coordinator ? 1 : 2);
  const sorted = [...data.members].sort((a, b) => roleRank(a) - roleRank(b) || (a.name || "").localeCompare(b.name || ""));
  const groups = {
    org_admin: sorted.filter((m) => m.is_org_admin),
    coordinator: sorted.filter((m) => m.is_coordinator && !m.is_org_admin),
    teacher: sorted.filter((m) => !m.is_coordinator && !m.is_org_admin),
  };

  const RoleBadge = ({ label, tint }) => (
    <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: tint, color: "#FFFFFF" }}>{label}</span>
  );

  const renderRow = (m) => (
    <div key={m.user_id || m.teacher_id} className="flex items-center justify-between gap-3 py-2 px-3" style={{ background: "var(--bg-base)" }} data-testid={`team-row-${m.user_id || m.teacher_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm" style={{ color: "var(--ink)" }}>{m.name || "(no name)"}</span>
          {m.is_org_admin && <RoleBadge label="Org Admin" tint="var(--ember)" />}
          {m.is_coordinator && !m.is_org_admin && <RoleBadge label="Coordinator" tint="var(--terracotta)" />}
          {!m.is_coordinator && !m.is_org_admin && <RoleBadge label="Teacher" tint="var(--muted)" />}
          {!m.has_login && <RoleBadge label="No login" tint="var(--muted)" />}
        </div>
        <div className="text-xs" style={{ color: "var(--muted)" }}>{m.email}</div>
      </div>
      {m.has_login && !m.is_org_admin && m.user_id && (
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none shrink-0" data-testid={`team-toggle-coord-${m.user_id}`}>
          <input
            type="checkbox"
            checked={!!m.is_coordinator}
            onChange={(e) => toggleCoord(m.user_id, e.target.checked)}
            className="h-4 w-4"
            style={{ accentColor: "var(--terracotta)" }}
          />
          <span style={{ color: "var(--ink-2)" }}>Coordinator</span>
        </label>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="team-roster-panel">
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Organization admins</h3>
          <span className="text-xs" style={{ color: "var(--muted)" }}>{groups.org_admin.length} / 2</span>
        </div>
        {groups.org_admin.length === 0 ? (
          <p className="text-sm italic" style={{ color: "var(--muted)" }}>No org admins yet. Ask a site admin to add one.</p>
        ) : (
          <div className="space-y-2">{groups.org_admin.map(renderRow)}</div>
        )}
      </div>

      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Coordinators</h3>
          <span className="text-xs" style={{ color: "var(--muted)" }}>{groups.coordinator.length}</span>
        </div>
        {groups.coordinator.length === 0 ? (
          <p className="text-sm italic" style={{ color: "var(--muted)" }}>No coordinators yet. Toggle the coordinator switch on a teacher below to promote them.</p>
        ) : (
          <div className="space-y-2">{groups.coordinator.map(renderRow)}</div>
        )}
      </div>

      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Teachers</h3>
          <span className="text-xs" style={{ color: "var(--muted)" }}>{groups.teacher.length}</span>
        </div>
        {groups.teacher.length === 0 ? (
          <p className="text-sm italic" style={{ color: "var(--muted)" }}>No teachers yet. Add teachers from the Teachers tab.</p>
        ) : (
          <div className="space-y-2">{groups.teacher.map(renderRow)}</div>
        )}
      </div>
    </div>
  );
}

export function DirectoryPanel({ orgId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const load = () => {
    if (!orgId) return;
    setLoading(true);
    const params = { org_id: orgId };
    if (includeArchived) params.include_archived = true;
    Promise.all([
      api.get("/coord/guests", { params }),
      api.get("/coord/teachers", { params: { org_id: orgId } }),
    ]).then(([g, t]) => {
      setRows(g.data || []);
      setTeachers(t.data || []);
    }).catch(() => toast.error("Couldn't load the directory."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId, includeArchived]);

  const filtered = useMemo(() => {
    const l = q.trim().toLowerCase();
    const stageOrder = { guest: 0, disciple: 1, member: 2 };
    return rows
      .filter((g) => (status === "all" ? true : (g.lifecycle_status || "guest") === status))
      .filter((g) => !l ||
        `${g.first_name} ${g.last_name}`.toLowerCase().includes(l) ||
        (g.phone || "").includes(l) ||
        (g.email || "").toLowerCase().includes(l))
      .sort((a, b) => {
        const sa = stageOrder[a.lifecycle_status || "guest"] ?? 0;
        const sb = stageOrder[b.lifecycle_status || "guest"] ?? 0;
        if (sa !== sb) return sa - sb;
        return `${a.last_name || ""}${a.first_name || ""}`.toLowerCase().localeCompare(`${b.last_name || ""}${b.first_name || ""}`.toLowerCase());
      });
  }, [rows, q, status]);

  const counts = useMemo(() => {
    const c = { all: rows.length, guest: 0, disciple: 0, member: 0, archived: 0 };
    rows.forEach((g) => {
      const s = g.lifecycle_status || "guest";
      if (c[s] !== undefined) c[s] += 1;
      if (g.archived) c.archived += 1;
    });
    return c;
  }, [rows]);

  const statusChip = (s) => {
    const meta = { guest: { label: "Guest", tint: "var(--muted)" }, disciple: { label: "Disciple", tint: "var(--orange)" }, member: { label: "Member", tint: "var(--gold)" } }[s] || { label: "Guest", tint: "var(--muted)" };
    return <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: meta.tint, color: "#FFFFFF" }} data-testid={`dir-status-${s}`}>{meta.label}</span>;
  };

  const selected = rows.find((r) => r.id === selectedId);
  const teacherGuestIds = useMemo(() => new Set(teachers.map((t) => t.guest_id).filter(Boolean)), [teachers]);

  return (
    <div data-testid="directory-panel">
      <ReviewQueue orgId={orgId} onChanged={load} />
      <div className="lg:sticky lg:top-[73px] z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 mb-3 space-y-3" style={{ background: "color-mix(in srgb, var(--bg-base) 92%, transparent)", backdropFilter: "blur(8px)" }}>
        <TrackerToolbar
          testidPrefix="dir"
          searchTestid="dir-search"
          search={q}
          onSearch={setQ}
          placeholder="Search everyone…"
          more={[{ label: includeArchived ? "Hide archived" : "Show archived", icon: Archive, onClick: () => setIncludeArchived(!includeArchived), testid: "dir-toggle-archived" }]}
          primary={<button onClick={() => setAddOpen(true)} className="btn-primary h-10 shrink-0" style={{ padding: "0 1.1rem" }} data-testid="dir-add-btn"><Plus size={15} /> <span className="hidden sm:inline">Add person</span></button>}
        />
        <TabBar testidPrefix="dir-filter" value={status} onChange={setStatus} tabs={[
          { key: "all", label: "Everyone", count: counts.all },
          { key: "guest", label: "Guests", count: counts.guest },
          { key: "disciple", label: "Disciples", count: counts.disciple, color: "var(--terracotta)" },
          { key: "member", label: "Members", count: counts.member, color: "var(--gold)" },
        ]} />
      </div>

      {loading ? (
        <p style={{ color: "var(--muted)" }}>Loading directory…</p>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <Users size={28} style={{ color: "var(--muted)", margin: "0 auto" }} />
          <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>Nobody matches those filters.</p>
        </div>
      ) : (
        <div className="rounded-md overflow-hidden" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="dir-list">
          {filtered.map((g, i) => (
            <button
              key={g.id}
              onClick={() => setSelectedId(g.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-[var(--surface-hover)] transition-colors"
              style={{ borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)" }}
              data-testid={`dir-row-${g.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium" style={{ color: "var(--ink)" }}>{formatDisplayName(g, { lastFirst: true })}</span>
                  {statusChip(g.lifecycle_status || "guest")}
                  {teacherGuestIds.has(g.id) && <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: "var(--olive)", color: "#FFFFFF" }} data-testid={`dir-teacher-badge-${g.id}`}>Teacher</span>}
                  {g.archived && <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: "var(--muted)", color: "#FFFFFF" }}>Archived</span>}
                </div>
                <div className="text-[0.7rem] mt-0.5" style={{ color: "var(--muted)" }}>{g.phone || g.email || ""}</div>
              </div>
              <ChevronRight size={14} style={{ color: "var(--muted)" }} />
            </button>
          ))}
        </div>
      )}

      {selected && (
        <GuestProfile
          guest={selected}
          teachers={teachers}
          onClose={() => setSelectedId(null)}
          onSaved={load}
          orgId={orgId}
        />
      )}
      {addOpen && (
        <AddDirectoryPersonDialog
          orgId={orgId}
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); load(); }}
        />
      )}
    </div>
  );
}
const TAB_PATHS = {
  "first-steps": "/crm/first-steps",
  "next-steps": "/crm/next-steps",
  "teachers": "/crm/teachers",
  "team-activity": "/crm/team-activity",
  "team-roster": "/crm/team",
  "directory": "/crm/directory",
};
const PATH_TO_TAB = Object.fromEntries(Object.entries(TAB_PATHS).map(([k, v]) => [v, k]));

export default function CoordinatorDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = PATH_TO_TAB[location.pathname] || "first-steps";
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/crm/login?next=/crm"); return; }
    if (!user.is_admin && !user.is_org_admin && !user.is_coordinator) {
      // Route to the right home for the role instead of the members paywall.
      if (user.is_section_leader) navigate("/attendance/section");
      else if (user.is_teacher) navigate("/teacher");
      else navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    if (user.is_admin) {
      api.get("/admin/orgs").then((r) => {
        setOrgs(r.data);
        if (r.data.length > 0 && !selectedOrgId) {
          const stored = localStorage.getItem("tdl_active_org_id");
          const found = r.data.find((o) => o.id === stored);
          setSelectedOrgId(found ? found.id : r.data[0].id);
        }
      });
    } else if (user.org_id) {
      setSelectedOrgId(user.org_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedOrgId && user?.is_admin) localStorage.setItem("tdl_active_org_id", selectedOrgId);
  }, [selectedOrgId, user]);

  if (loading || !user) {
    return <div className="max-w-7xl mx-auto px-6 py-24"><p style={{ color: "var(--muted)" }}>Loading…</p></div>;
  }
  if (!user.is_admin && !user.is_org_admin && !user.is_coordinator) return null;

  if (!user.is_admin && !user.org_id) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif-display text-3xl" style={{ color: "var(--ink)" }}>No organization assigned</h1>
        <p className="mt-4" style={{ color: "var(--ink-2)" }}>Your account isn't attached to a church organization yet. Please contact the site admin.</p>
      </div>
    );
  }

  const activeOrg = orgs.find((o) => o.id === selectedOrgId);
  if (!selectedOrgId) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24">
        <p style={{ color: "var(--muted)" }}>
          {user.is_admin ? "No organizations yet. Create one from the Admin page." : "Loading organization…"}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-12" data-testid="coord-page">
      <header className="mb-8">
        <div className="mb-6">
          <ChurchToolsSwitcher />
        </div>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="overline mb-2">Bible study coordinator</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>
              Guest <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>tracker</span>
            </h1>
            <p className="mt-3 text-sm hidden sm:block" style={{ color: "var(--ink-2)" }}>
              Introduction → Church Study → Home Study → Next Steps. Tap a guest to see their lessons, or tap Done to check off their next one.
            </p>
          </div>

          <div className="min-w-[15rem]" data-testid="org-switcher">
            <div className="overline mb-1" style={{ fontSize: "0.6rem" }}>Organization</div>
            {user.is_admin ? (
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                data-testid="org-switcher-select"
                className="w-full px-3 py-2 text-sm rounded-md"
              >
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            ) : (
              <div className="px-3 py-2 text-sm rounded-md border" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)", color: "var(--ink)" }}>
                {activeOrg?.name || "…"}
              </div>
            )}
          </div>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={(v) => navigate(TAB_PATHS[v] || "/crm/first-steps")}>
        <TabsList className="bg-transparent border-b w-full justify-start rounded-none gap-1 sm:gap-2 h-auto p-0 flex-nowrap overflow-x-auto overflow-y-hidden" style={{ borderColor: "var(--border-subtle)" }}>
          <TabsTrigger value="first-steps" data-testid="coord-tab-first-steps" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--ember)] data-[state=active]:text-[var(--ink)] px-3 sm:px-4 py-3 shrink-0">First Steps</TabsTrigger>
          <TabsTrigger value="next-steps" data-testid="coord-tab-next-steps" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--gold)] data-[state=active]:text-[var(--ink)] px-3 sm:px-4 py-3 shrink-0">Next Steps</TabsTrigger>
          <TabsTrigger value="directory" data-testid="coord-tab-directory" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--gold)] data-[state=active]:text-[var(--ink)] px-3 sm:px-4 py-3 shrink-0">Directory</TabsTrigger>
          <TabsTrigger value="teachers" data-testid="coord-tab-teachers" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--ember)] data-[state=active]:text-[var(--ink)] px-3 sm:px-4 py-3 shrink-0">Teachers</TabsTrigger>
          <TabsTrigger value="team-activity" data-testid="coord-tab-team" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--ember)] data-[state=active]:text-[var(--ink)] px-3 sm:px-4 py-3 shrink-0">Team activity</TabsTrigger>
          {(user.is_admin || user.is_org_admin) && (
            <TabsTrigger value="team-roster" data-testid="coord-tab-team-roster" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--ember)] data-[state=active]:text-[var(--ink)] px-3 sm:px-4 py-3 shrink-0">Team roster</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="first-steps" className="mt-8"><GuestsPanel orgId={selectedOrgId} /></TabsContent>
        <TabsContent value="next-steps" className="mt-8"><NextStepsPanel orgId={selectedOrgId} /></TabsContent>
        <TabsContent value="directory" className="mt-8"><DirectoryPanel orgId={selectedOrgId} /></TabsContent>
        <TabsContent value="teachers" className="mt-8"><TeachersPanel orgId={selectedOrgId} /></TabsContent>
        <TabsContent value="team-activity" className="mt-8"><TeamActivityPanel orgId={selectedOrgId} /></TabsContent>
        {(user.is_admin || user.is_org_admin) && (
          <TabsContent value="team-roster" className="mt-8"><TeamRosterPanel orgId={selectedOrgId} isSysAdmin={!!user.is_admin} /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

